<template>
  <div class="address-selector address-selector--glass">
    <LabelSearchableSelect
      custom-class="address-selector-field"
      label="Country"
      select-id="address-selector-country"
      :model-value="selectedCountry"
      :options="countryOptions"
      placeholder="Select Country"
      option-value="code"
      option-label="name"
      :required="required"
      :disabled="disabled"
      @update:modelValue="onCountryChange"
    />

    <LabelSearchableSelect
      custom-class="address-selector-field"
      label="State"
      select-id="address-selector-state"
      :model-value="selectedState"
      :options="availableStates"
      placeholder="Select State"
      option-value="code"
      option-label="name"
      :required="required"
      :disabled="disabled || !selectedCountry || !availableStates.length"
      @update:modelValue="onStateChange"
    />

    <LabelSearchableSelect
      v-if="availableCities.length > 0"
      custom-class="address-selector-field"
      label="City"
      select-id="address-selector-city"
      :model-value="selectedCity"
      :options="cityOptions"
      placeholder="Select City"
      option-value="name"
      option-label="name"
      :required="required"
      :disabled="disabled || !selectedState"
      @update:modelValue="onCityChange"
    />
    <LabelInputField
      v-else
      custom-class="address-selector-field address-selector-field--text"
      label="City"
      input-id="address-selector-city"
      v-model="selectedCity"
      placeholder="Enter city name"
      :required="required"
      :disabled="disabled || !selectedState"
      @input="onCityChange"
    />

    <LabelInputField
      custom-class="address-selector-field address-selector-field--text"
      label="Postal Code"
      input-id="address-selector-postal"
      v-model="selectedPostalCode"
      :placeholder="postalCodePlaceholder"
      :required="required"
      :disabled="disabled"
      :pattern="postalCodePattern || undefined"
      :has-error="!!postalCodeError"
      @input="onPostalCodeChange"
      @blur="validatePostalCode"
    >
      <div v-if="postalCodeError" class="address-selector-error-message">
        {{ postalCodeError }}
      </div>
      <div v-if="postalCodeValid && selectedPostalCode && !postalCodeError" class="address-selector-success-message">
        ✓ Valid postal code
      </div>
    </LabelInputField>
  </div>
</template>

<script>
import LabelSearchableSelect from '@/components/LabelSearchableSelect.vue';
import LabelInputField from '@/modules/consultant/components/LabelInputField.vue';
import zipcodes from 'zipcodes';
import addresser from 'addresser';

export default {
  name: 'AddressSelector',
  components: {
    LabelSearchableSelect,
    LabelInputField,
  },
  emits: ['update:country', 'update:state', 'update:city', 'update:postalCode', 'address-change'],
  props: {
    country: {
      type: String,
      default: 'US'
    },
    state: {
      type: String,
      default: ''
    },
    city: {
      type: String,
      default: ''
    },
    postalCode: {
      type: String,
      default: ''
    },
    required: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      selectedCountry: this.country || 'US',
      selectedState: this.state,
      selectedCity: this.city,
      selectedPostalCode: this.postalCode,
      postalCodeError: null,
      postalCodeValid: false,
      countryOptions: [
        { code: 'US', name: 'United States' },
        { code: 'CA', name: 'Canada' },
        { code: 'GB', name: 'United Kingdom' },
        { code: 'AU', name: 'Australia' },
        { code: 'DE', name: 'Germany' },
        { code: 'FR', name: 'France' },
        { code: 'IN', name: 'India' },
        { code: 'JP', name: 'Japan' },
        { code: 'CN', name: 'China' },
        { code: 'BR', name: 'Brazil' },
        { code: 'MX', name: 'Mexico' },
        { code: 'IT', name: 'Italy' },
        { code: 'ES', name: 'Spain' },
        { code: 'NL', name: 'Netherlands' },
        { code: 'SE', name: 'Sweden' },
        { code: 'NO', name: 'Norway' },
        { code: 'DK', name: 'Denmark' },
        { code: 'FI', name: 'Finland' },
        { code: 'CH', name: 'Switzerland' },
        { code: 'AT', name: 'Austria' },
        { code: 'BE', name: 'Belgium' },
        { code: 'PT', name: 'Portugal' },
        { code: 'GR', name: 'Greece' },
        { code: 'PL', name: 'Poland' },
        { code: 'CZ', name: 'Czech Republic' },
        { code: 'HU', name: 'Hungary' },
        { code: 'RO', name: 'Romania' },
        { code: 'BG', name: 'Bulgaria' },
        { code: 'HR', name: 'Croatia' },
        { code: 'IE', name: 'Ireland' },
        { code: 'IS', name: 'Iceland' },
        { code: 'LU', name: 'Luxembourg' },
        { code: 'MT', name: 'Malta' },
        { code: 'SK', name: 'Slovakia' },
        { code: 'SI', name: 'Slovenia' },
        { code: 'EE', name: 'Estonia' },
        { code: 'LV', name: 'Latvia' },
        { code: 'LT', name: 'Lithuania' },
        { code: 'RU', name: 'Russia' },
        { code: 'UA', name: 'Ukraine' },
        { code: 'TR', name: 'Turkey' },
        { code: 'SA', name: 'Saudi Arabia' },
        { code: 'AE', name: 'United Arab Emirates' },
        { code: 'IL', name: 'Israel' },
        { code: 'ZA', name: 'South Africa' },
        { code: 'EG', name: 'Egypt' },
        { code: 'NG', name: 'Nigeria' },
        { code: 'KE', name: 'Kenya' },
        { code: 'AR', name: 'Argentina' },
        { code: 'CL', name: 'Chile' },
        { code: 'CO', name: 'Colombia' },
        { code: 'PE', name: 'Peru' },
        { code: 'VE', name: 'Venezuela' },
        { code: 'EC', name: 'Ecuador' },
        { code: 'UY', name: 'Uruguay' },
        { code: 'PY', name: 'Paraguay' },
        { code: 'BO', name: 'Bolivia' },
        { code: 'KR', name: 'South Korea' },
        { code: 'TH', name: 'Thailand' },
        { code: 'VN', name: 'Vietnam' },
        { code: 'MY', name: 'Malaysia' },
        { code: 'SG', name: 'Singapore' },
        { code: 'PH', name: 'Philippines' },
        { code: 'ID', name: 'Indonesia' },
        { code: 'PK', name: 'Pakistan' },
        { code: 'BD', name: 'Bangladesh' },
        { code: 'LK', name: 'Sri Lanka' },
        { code: 'NZ', name: 'New Zealand' }
      ],
      usStates: [
        { code: 'AL', name: 'Alabama' },
        { code: 'AK', name: 'Alaska' },
        { code: 'AZ', name: 'Arizona' },
        { code: 'AR', name: 'Arkansas' },
        { code: 'CA', name: 'California' },
        { code: 'CO', name: 'Colorado' },
        { code: 'CT', name: 'Connecticut' },
        { code: 'DE', name: 'Delaware' },
        { code: 'FL', name: 'Florida' },
        { code: 'GA', name: 'Georgia' },
        { code: 'HI', name: 'Hawaii' },
        { code: 'ID', name: 'Idaho' },
        { code: 'IL', name: 'Illinois' },
        { code: 'IN', name: 'Indiana' },
        { code: 'IA', name: 'Iowa' },
        { code: 'KS', name: 'Kansas' },
        { code: 'KY', name: 'Kentucky' },
        { code: 'LA', name: 'Louisiana' },
        { code: 'ME', name: 'Maine' },
        { code: 'MD', name: 'Maryland' },
        { code: 'MA', name: 'Massachusetts' },
        { code: 'MI', name: 'Michigan' },
        { code: 'MN', name: 'Minnesota' },
        { code: 'MS', name: 'Mississippi' },
        { code: 'MO', name: 'Missouri' },
        { code: 'MT', name: 'Montana' },
        { code: 'NE', name: 'Nebraska' },
        { code: 'NV', name: 'Nevada' },
        { code: 'NH', name: 'New Hampshire' },
        { code: 'NJ', name: 'New Jersey' },
        { code: 'NM', name: 'New Mexico' },
        { code: 'NY', name: 'New York' },
        { code: 'NC', name: 'North Carolina' },
        { code: 'ND', name: 'North Dakota' },
        { code: 'OH', name: 'Ohio' },
        { code: 'OK', name: 'Oklahoma' },
        { code: 'OR', name: 'Oregon' },
        { code: 'PA', name: 'Pennsylvania' },
        { code: 'RI', name: 'Rhode Island' },
        { code: 'SC', name: 'South Carolina' },
        { code: 'SD', name: 'South Dakota' },
        { code: 'TN', name: 'Tennessee' },
        { code: 'TX', name: 'Texas' },
        { code: 'UT', name: 'Utah' },
        { code: 'VT', name: 'Vermont' },
        { code: 'VA', name: 'Virginia' },
        { code: 'WA', name: 'Washington' },
        { code: 'WV', name: 'West Virginia' },
        { code: 'WI', name: 'Wisconsin' },
        { code: 'WY', name: 'Wyoming' },
        { code: 'DC', name: 'District of Columbia' }
      ],
      canadianProvinces: [
        { code: 'AB', name: 'Alberta' },
        { code: 'BC', name: 'British Columbia' },
        { code: 'MB', name: 'Manitoba' },
        { code: 'NB', name: 'New Brunswick' },
        { code: 'NL', name: 'Newfoundland and Labrador' },
        { code: 'NS', name: 'Nova Scotia' },
        { code: 'ON', name: 'Ontario' },
        { code: 'PE', name: 'Prince Edward Island' },
        { code: 'QC', name: 'Quebec' },
        { code: 'SK', name: 'Saskatchewan' },
        { code: 'NT', name: 'Northwest Territories' },
        { code: 'NU', name: 'Nunavut' },
        { code: 'YT', name: 'Yukon' }
      ],
      ukRegions: [
        { code: 'ENG', name: 'England' },
        { code: 'SCT', name: 'Scotland' },
        { code: 'WLS', name: 'Wales' },
        { code: 'NIR', name: 'Northern Ireland' }
      ],
      australianStates: [
        { code: 'NSW', name: 'New South Wales' },
        { code: 'VIC', name: 'Victoria' },
        { code: 'QLD', name: 'Queensland' },
        { code: 'WA', name: 'Western Australia' },
        { code: 'SA', name: 'South Australia' },
        { code: 'TAS', name: 'Tasmania' },
        { code: 'ACT', name: 'Australian Capital Territory' },
        { code: 'NT', name: 'Northern Territory' }
      ],
      germanStates: [
        { code: 'BW', name: 'Baden-Württemberg' },
        { code: 'BY', name: 'Bavaria' },
        { code: 'BE', name: 'Berlin' },
        { code: 'BB', name: 'Brandenburg' },
        { code: 'HB', name: 'Bremen' },
        { code: 'HH', name: 'Hamburg' },
        { code: 'HE', name: 'Hesse' },
        { code: 'MV', name: 'Mecklenburg-Vorpommern' },
        { code: 'NI', name: 'Lower Saxony' },
        { code: 'NW', name: 'North Rhine-Westphalia' },
        { code: 'RP', name: 'Rhineland-Palatinate' },
        { code: 'SL', name: 'Saarland' },
        { code: 'SN', name: 'Saxony' },
        { code: 'ST', name: 'Saxony-Anhalt' },
        { code: 'SH', name: 'Schleswig-Holstein' },
        { code: 'TH', name: 'Thuringia' }
      ],
      frenchRegions: [
        { code: 'ARA', name: 'Auvergne-Rhône-Alpes' },
        { code: 'BFC', name: 'Bourgogne-Franche-Comté' },
        { code: 'BRE', name: 'Brittany' },
        { code: 'CVL', name: 'Centre-Val de Loire' },
        { code: 'COR', name: 'Corsica' },
        { code: 'GES', name: 'Grand Est' },
        { code: 'HDF', name: 'Hauts-de-France' },
        { code: 'IDF', name: 'Île-de-France' },
        { code: 'NOR', name: 'Normandy' },
        { code: 'NAQ', name: 'Nouvelle-Aquitaine' },
        { code: 'OCC', name: 'Occitanie' },
        { code: 'PDL', name: 'Pays de la Loire' },
        { code: 'PAC', name: "Provence-Alpes-Côte d'Azur" }
      ],
      indianStates: [
        { code: 'AP', name: 'Andhra Pradesh' },
        { code: 'AR', name: 'Arunachal Pradesh' },
        { code: 'AS', name: 'Assam' },
        { code: 'BR', name: 'Bihar' },
        { code: 'CT', name: 'Chhattisgarh' },
        { code: 'GA', name: 'Goa' },
        { code: 'GJ', name: 'Gujarat' },
        { code: 'HR', name: 'Haryana' },
        { code: 'HP', name: 'Himachal Pradesh' },
        { code: 'JK', name: 'Jammu and Kashmir' },
        { code: 'JH', name: 'Jharkhand' },
        { code: 'KA', name: 'Karnataka' },
        { code: 'KL', name: 'Kerala' },
        { code: 'MP', name: 'Madhya Pradesh' },
        { code: 'MH', name: 'Maharashtra' },
        { code: 'MN', name: 'Manipur' },
        { code: 'ML', name: 'Meghalaya' },
        { code: 'MZ', name: 'Mizoram' },
        { code: 'NL', name: 'Nagaland' },
        { code: 'OR', name: 'Odisha' },
        { code: 'PB', name: 'Punjab' },
        { code: 'RJ', name: 'Rajasthan' },
        { code: 'SK', name: 'Sikkim' },
        { code: 'TN', name: 'Tamil Nadu' },
        { code: 'TG', name: 'Telangana' },
        { code: 'TR', name: 'Tripura' },
        { code: 'UP', name: 'Uttar Pradesh' },
        { code: 'UT', name: 'Uttarakhand' },
        { code: 'WB', name: 'West Bengal' },
        { code: 'AN', name: 'Andaman and Nicobar Islands' },
        { code: 'CH', name: 'Chandigarh' },
        { code: 'DH', name: 'Dadra and Nagar Haveli' },
        { code: 'DD', name: 'Daman and Diu' },
        { code: 'DL', name: 'Delhi' },
        { code: 'LD', name: 'Lakshadweep' },
        { code: 'PY', name: 'Puducherry' }
      ],
      japanesePrefectures: [
        { code: 'HOK', name: 'Hokkaido' },
        { code: 'AOM', name: 'Aomori' },
        { code: 'IWA', name: 'Iwate' },
        { code: 'MIY', name: 'Miyagi' },
        { code: 'AKI', name: 'Akita' },
        { code: 'YAM', name: 'Yamagata' },
        { code: 'FUK', name: 'Fukushima' },
        { code: 'IB', name: 'Ibaraki' },
        { code: 'TOCH', name: 'Tochigi' },
        { code: 'GUN', name: 'Gunma' },
        { code: 'SAI', name: 'Saitama' },
        { code: 'CHI', name: 'Chiba' },
        { code: 'TOK', name: 'Tokyo' },
        { code: 'KAN', name: 'Kanagawa' },
        { code: 'NII', name: 'Niigata' },
        { code: 'TOY', name: 'Toyama' },
        { code: 'ISH', name: 'Ishikawa' },
        { code: 'FUKU', name: 'Fukui' },
        { code: 'YAMAN', name: 'Yamanashi' },
        { code: 'NAG', name: 'Nagano' },
        { code: 'GIF', name: 'Gifu' },
        { code: 'SHI', name: 'Shizuoka' },
        { code: 'AIC', name: 'Aichi' },
        { code: 'MIE', name: 'Mie' },
        { code: 'SHIG', name: 'Shiga' },
        { code: 'KYO', name: 'Kyoto' },
        { code: 'OSA', name: 'Osaka' },
        { code: 'HYO', name: 'Hyogo' },
        { code: 'NAR', name: 'Nara' },
        { code: 'WAK', name: 'Wakayama' },
        { code: 'TOT', name: 'Tottori' },
        { code: 'SHIM', name: 'Shimane' },
        { code: 'OKA', name: 'Okayama' },
        { code: 'HIR', name: 'Hiroshima' },
        { code: 'YAMAG', name: 'Yamaguchi' },
        { code: 'TOKUSH', name: 'Tokushima' },
        { code: 'KAG', name: 'Kagawa' },
        { code: 'EHI', name: 'Ehime' },
        { code: 'KOC', name: 'Kochi' },
        { code: 'FUKUO', name: 'Fukuoka' },
        { code: 'SAG', name: 'Saga' },
        { code: 'NAGAS', name: 'Nagasaki' },
        { code: 'KUM', name: 'Kumamoto' },
        { code: 'OIT', name: 'Oita' },
        { code: 'MIYAZ', name: 'Miyazaki' },
        { code: 'KAGO', name: 'Kagoshima' },
        { code: 'OKI', name: 'Okinawa' }
      ],
      chineseProvinces: [
        { code: 'BJ', name: 'Beijing' },
        { code: 'TJ', name: 'Tianjin' },
        { code: 'HE', name: 'Hebei' },
        { code: 'SX', name: 'Shanxi' },
        { code: 'NM', name: 'Inner Mongolia' },
        { code: 'LN', name: 'Liaoning' },
        { code: 'JL', name: 'Jilin' },
        { code: 'HL', name: 'Heilongjiang' },
        { code: 'SH', name: 'Shanghai' },
        { code: 'JS', name: 'Jiangsu' },
        { code: 'ZJ', name: 'Zhejiang' },
        { code: 'AH', name: 'Anhui' },
        { code: 'FJ', name: 'Fujian' },
        { code: 'JX', name: 'Jiangxi' },
        { code: 'SD', name: 'Shandong' },
        { code: 'HA', name: 'Henan' },
        { code: 'HB', name: 'Hubei' },
        { code: 'HN', name: 'Hunan' },
        { code: 'GD', name: 'Guangdong' },
        { code: 'GX', name: 'Guangxi' },
        { code: 'HI', name: 'Hainan' },
        { code: 'CQ', name: 'Chongqing' },
        { code: 'SC', name: 'Sichuan' },
        { code: 'GZ', name: 'Guizhou' },
        { code: 'YN', name: 'Yunnan' },
        { code: 'XZ', name: 'Tibet' },
        { code: 'SN', name: 'Shaanxi' },
        { code: 'GS', name: 'Gansu' },
        { code: 'QH', name: 'Qinghai' },
        { code: 'NX', name: 'Ningxia' },
        { code: 'XJ', name: 'Xinjiang' },
        { code: 'HK', name: 'Hong Kong' },
        { code: 'MO', name: 'Macau' },
        { code: 'TW', name: 'Taiwan' }
      ],
      brazilianStates: [
        { code: 'AC', name: 'Acre' },
        { code: 'AL', name: 'Alagoas' },
        { code: 'AP', name: 'Amapá' },
        { code: 'AM', name: 'Amazonas' },
        { code: 'BA', name: 'Bahia' },
        { code: 'CE', name: 'Ceará' },
        { code: 'DF', name: 'Distrito Federal' },
        { code: 'ES', name: 'Espírito Santo' },
        { code: 'GO', name: 'Goiás' },
        { code: 'MA', name: 'Maranhão' },
        { code: 'MT', name: 'Mato Grosso' },
        { code: 'MS', name: 'Mato Grosso do Sul' },
        { code: 'MG', name: 'Minas Gerais' },
        { code: 'PA', name: 'Pará' },
        { code: 'PB', name: 'Paraíba' },
        { code: 'PR', name: 'Paraná' },
        { code: 'PE', name: 'Pernambuco' },
        { code: 'PI', name: 'Piauí' },
        { code: 'RJ', name: 'Rio de Janeiro' },
        { code: 'RN', name: 'Rio Grande do Norte' },
        { code: 'RS', name: 'Rio Grande do Sul' },
        { code: 'RO', name: 'Rondônia' },
        { code: 'RR', name: 'Roraima' },
        { code: 'SC', name: 'Santa Catarina' },
        { code: 'SP', name: 'São Paulo' },
        { code: 'SE', name: 'Sergipe' },
        { code: 'TO', name: 'Tocantins' }
      ],
      mexicanStates: [
        { code: 'AGU', name: 'Aguascalientes' },
        { code: 'BCN', name: 'Baja California' },
        { code: 'BCS', name: 'Baja California Sur' },
        { code: 'CAM', name: 'Campeche' },
        { code: 'CHP', name: 'Chiapas' },
        { code: 'CHH', name: 'Chihuahua' },
        { code: 'COA', name: 'Coahuila' },
        { code: 'COL', name: 'Colima' },
        { code: 'DIF', name: 'Mexico City' },
        { code: 'DUR', name: 'Durango' },
        { code: 'GUA', name: 'Guanajuato' },
        { code: 'GRO', name: 'Guerrero' },
        { code: 'HID', name: 'Hidalgo' },
        { code: 'JAL', name: 'Jalisco' },
        { code: 'MEX', name: 'Mexico' },
        { code: 'MIC', name: 'Michoacán' },
        { code: 'MOR', name: 'Morelos' },
        { code: 'NAY', name: 'Nayarit' },
        { code: 'NLE', name: 'Nuevo León' },
        { code: 'OAX', name: 'Oaxaca' },
        { code: 'PUE', name: 'Puebla' },
        { code: 'QUE', name: 'Querétaro' },
        { code: 'ROO', name: 'Quintana Roo' },
        { code: 'SLP', name: 'San Luis Potosí' },
        { code: 'SIN', name: 'Sinaloa' },
        { code: 'SON', name: 'Sonora' },
        { code: 'TAB', name: 'Tabasco' },
        { code: 'TAM', name: 'Tamaulipas' },
        { code: 'TLA', name: 'Tlaxcala' },
        { code: 'VER', name: 'Veracruz' },
        { code: 'YUC', name: 'Yucatán' },
        { code: 'ZAC', name: 'Zacatecas' }
      ],
      italianRegions: [
        { code: 'ABR', name: 'Abruzzo' },
        { code: 'BAS', name: 'Basilicata' },
        { code: 'CAL', name: 'Calabria' },
        { code: 'CAM', name: 'Campania' },
        { code: 'EMR', name: 'Emilia-Romagna' },
        { code: 'FVG', name: 'Friuli-Venezia Giulia' },
        { code: 'LAZ', name: 'Lazio' },
        { code: 'LIG', name: 'Liguria' },
        { code: 'LOM', name: 'Lombardy' },
        { code: 'MAR', name: 'Marche' },
        { code: 'MOL', name: 'Molise' },
        { code: 'PIE', name: 'Piedmont' },
        { code: 'PUG', name: 'Apulia' },
        { code: 'SAR', name: 'Sardinia' },
        { code: 'SIC', name: 'Sicily' },
        { code: 'TOS', name: 'Tuscany' },
        { code: 'TAA', name: 'Trentino-Alto Adige' },
        { code: 'UMB', name: 'Umbria' },
        { code: 'VAL', name: 'Aosta Valley' },
        { code: 'VEN', name: 'Veneto' }
      ],
      spanishProvinces: [
        { code: 'AL', name: 'Almería' },
        { code: 'CA', name: 'Cádiz' },
        { code: 'CO', name: 'Córdoba' },
        { code: 'GR', name: 'Granada' },
        { code: 'HU', name: 'Huelva' },
        { code: 'JA', name: 'Jaén' },
        { code: 'MA', name: 'Málaga' },
        { code: 'SE', name: 'Seville' },
        { code: 'B', name: 'Barcelona' },
        { code: 'GI', name: 'Girona' },
        { code: 'L', name: 'Lleida' },
        { code: 'T', name: 'Tarragona' },
        { code: 'AB', name: 'Albacete' },
        { code: 'CR', name: 'Ciudad Real' },
        { code: 'CU', name: 'Cuenca' },
        { code: 'GU', name: 'Guadalajara' },
        { code: 'TO', name: 'Toledo' },
        { code: 'AV', name: 'Ávila' },
        { code: 'BU', name: 'Burgos' },
        { code: 'LE', name: 'León' },
        { code: 'P', name: 'Palencia' },
        { code: 'SA', name: 'Salamanca' },
        { code: 'SG', name: 'Segovia' },
        { code: 'SO', name: 'Soria' },
        { code: 'VA', name: 'Valladolid' },
        { code: 'ZA', name: 'Zamora' },
        { code: 'BI', name: 'Biscay' },
        { code: 'SS', name: 'Gipuzkoa' },
        { code: 'VI', name: 'Álava' },
        { code: 'LO', name: 'La Rioja' },
        { code: 'M', name: 'Madrid' },
        { code: 'MU', name: 'Murcia' },
        { code: 'NA', name: 'Navarre' },
        { code: 'O', name: 'Asturias' },
        { code: 'PM', name: 'Balearic Islands' },
        { code: 'TF', name: 'Santa Cruz de Tenerife' },
        { code: 'GC', name: 'Las Palmas' },
        { code: 'CE', name: 'Ceuta' },
        { code: 'ML', name: 'Melilla' }
      ],
      dutchProvinces: [
        { code: 'DR', name: 'Drenthe' },
        { code: 'FL', name: 'Flevoland' },
        { code: 'FR', name: 'Friesland' },
        { code: 'GE', name: 'Gelderland' },
        { code: 'GR', name: 'Groningen' },
        { code: 'LI', name: 'Limburg' },
        { code: 'NB', name: 'North Brabant' },
        { code: 'NH', name: 'North Holland' },
        { code: 'OV', name: 'Overijssel' },
        { code: 'UT', name: 'Utrecht' },
        { code: 'ZE', name: 'Zeeland' },
        { code: 'ZH', name: 'South Holland' }
      ],
      swedishCounties: [
        { code: 'ST', name: 'Stockholm' },
        { code: 'VG', name: 'Västra Götaland' },
        { code: 'SK', name: 'Skåne' },
        { code: 'UP', name: 'Uppsala' },
        { code: 'SO', name: 'Södermanland' },
        { code: 'OG', name: 'Östergötland' },
        { code: 'JO', name: 'Jönköping' },
        { code: 'KR', name: 'Kronoberg' },
        { code: 'KA', name: 'Kalmar' },
        { code: 'GO', name: 'Gotland' },
        { code: 'BL', name: 'Blekinge' },
        { code: 'HA', name: 'Halland' },
        { code: 'VL', name: 'Västra Götaland' },
        { code: 'VB', name: 'Värmland' },
        { code: 'OR', name: 'Örebro' },
        { code: 'VS', name: 'Västmanland' },
        { code: 'DL', name: 'Dalarna' },
        { code: 'GA', name: 'Gävleborg' },
        { code: 'VN', name: 'Västernorrland' },
        { code: 'JA', name: 'Jämtland' },
        { code: 'VB', name: 'Västerbotten' },
        { code: 'NB', name: 'Norrbotten' }
      ],
      norwegianCounties: [
        { code: 'OS', name: 'Oslo' },
        { code: 'AK', name: 'Akershus' },
        { code: 'ØS', name: 'Østfold' },
        { code: 'HE', name: 'Hedmark' },
        { code: 'OP', name: 'Oppland' },
        { code: 'BU', name: 'Buskerud' },
        { code: 'VE', name: 'Vestfold' },
        { code: 'TE', name: 'Telemark' },
        { code: 'AA', name: 'Aust-Agder' },
        { code: 'VA', name: 'Vest-Agder' },
        { code: 'RO', name: 'Rogaland' },
        { code: 'HO', name: 'Hordaland' },
        { code: 'SF', name: 'Sogn og Fjordane' },
        { code: 'MR', name: 'Møre og Romsdal' },
        { code: 'ST', name: 'Sør-Trøndelag' },
        { code: 'NT', name: 'Nord-Trøndelag' },
        { code: 'NO', name: 'Nordland' },
        { code: 'TR', name: 'Troms' },
        { code: 'FI', name: 'Finnmark' }
      ],
      danishRegions: [
        { code: 'H', name: 'Capital Region' },
        { code: 'M', name: 'Zealand' },
        { code: 'S', name: 'Southern Denmark' },
        { code: 'N', name: 'Central Denmark' },
        { code: 'N', name: 'North Denmark' }
      ],
      finnishRegions: [
        { code: '01', name: 'Åland' },
        { code: '02', name: 'South Karelia' },
        { code: '03', name: 'South Ostrobothnia' },
        { code: '04', name: 'South Savonia' },
        { code: '05', name: 'Kainuu' },
        { code: '06', name: 'Tavastia Proper' },
        { code: '07', name: 'Central Ostrobothnia' },
        { code: '08', name: 'Central Finland' },
        { code: '09', name: 'Kymenlaakso' },
        { code: '10', name: 'Lapland' },
        { code: '11', name: 'Pirkanmaa' },
        { code: '12', name: 'Ostrobothnia' },
        { code: '13', name: 'North Karelia' },
        { code: '14', name: 'North Ostrobothnia' },
        { code: '15', name: 'North Savonia' },
        { code: '16', name: 'Päijänne Tavastia' },
        { code: '17', name: 'Satakunta' },
        { code: '18', name: 'Uusimaa' },
        { code: '19', name: 'Southwest Finland' }
      ],
      swissCantons: [
        { code: 'AG', name: 'Aargau' },
        { code: 'AI', name: 'Appenzell Innerrhoden' },
        { code: 'AR', name: 'Appenzell Ausserrhoden' },
        { code: 'BE', name: 'Bern' },
        { code: 'BL', name: 'Basel-Landschaft' },
        { code: 'BS', name: 'Basel-Stadt' },
        { code: 'FR', name: 'Fribourg' },
        { code: 'GE', name: 'Geneva' },
        { code: 'GL', name: 'Glarus' },
        { code: 'GR', name: 'Graubünden' },
        { code: 'JU', name: 'Jura' },
        { code: 'LU', name: 'Lucerne' },
        { code: 'NE', name: 'Neuchâtel' },
        { code: 'NW', name: 'Nidwalden' },
        { code: 'OW', name: 'Obwalden' },
        { code: 'SG', name: 'St. Gallen' },
        { code: 'SH', name: 'Schaffhausen' },
        { code: 'SO', name: 'Solothurn' },
        { code: 'SZ', name: 'Schwyz' },
        { code: 'TG', name: 'Thurgau' },
        { code: 'TI', name: 'Ticino' },
        { code: 'UR', name: 'Uri' },
        { code: 'VD', name: 'Vaud' },
        { code: 'VS', name: 'Valais' },
        { code: 'ZG', name: 'Zug' },
        { code: 'ZH', name: 'Zurich' }
      ],
      austrianStates: [
        { code: 'B', name: 'Burgenland' },
        { code: 'K', name: 'Carinthia' },
        { code: 'NÖ', name: 'Lower Austria' },
        { code: 'OÖ', name: 'Upper Austria' },
        { code: 'S', name: 'Salzburg' },
        { code: 'ST', name: 'Styria' },
        { code: 'T', name: 'Tyrol' },
        { code: 'V', name: 'Vorarlberg' },
        { code: 'W', name: 'Vienna' }
      ],
      belgianProvinces: [
        { code: 'VAN', name: 'Antwerp' },
        { code: 'LIM', name: 'Limburg' },
        { code: 'OVL', name: 'East Flanders' },
        { code: 'VBR', name: 'Flemish Brabant' },
        { code: 'WVL', name: 'West Flanders' },
        { code: 'WAL', name: 'Walloon Brabant' },
        { code: 'HAI', name: 'Hainaut' },
        { code: 'LIE', name: 'Liège' },
        { code: 'LUX', name: 'Luxembourg' },
        { code: 'NAM', name: 'Namur' },
        { code: 'BRU', name: 'Brussels-Capital Region' }
      ],
      portugueseRegions: [
        { code: 'AV', name: 'Aveiro' },
        { code: 'BE', name: 'Beja' },
        { code: 'BR', name: 'Braga' },
        { code: 'CB', name: 'Castelo Branco' },
        { code: 'CO', name: 'Coimbra' },
        { code: 'EV', name: 'Évora' },
        { code: 'FA', name: 'Faro' },
        { code: 'GU', name: 'Guarda' },
        { code: 'LE', name: 'Leiria' },
        { code: 'LI', name: 'Lisbon' },
        { code: 'PO', name: 'Portalegre' },
        { code: 'PO', name: 'Porto' },
        { code: 'SA', name: 'Santarém' },
        { code: 'SE', name: 'Setúbal' },
        { code: 'VI', name: 'Viana do Castelo' },
        { code: 'VR', name: 'Vila Real' },
        { code: 'VI', name: 'Viseu' },
        { code: 'AC', name: 'Azores' },
        { code: 'MA', name: 'Madeira' }
      ],
      greekRegions: [
        { code: 'AT', name: 'Attica' },
        { code: 'CE', name: 'Central Greece' },
        { code: 'CM', name: 'Central Macedonia' },
        { code: 'CR', name: 'Crete' },
        { code: 'EM', name: 'East Macedonia and Thrace' },
        { code: 'EP', name: 'Epirus' },
        { code: 'IO', name: 'Ionian Islands' },
        { code: 'NA', name: 'North Aegean' },
        { code: 'PE', name: 'Peloponnese' },
        { code: 'SO', name: 'South Aegean' },
        { code: 'TH', name: 'Thessaly' },
        { code: 'WE', name: 'West Greece' },
        { code: 'WM', name: 'West Macedonia' }
      ],
      polishVoivodeships: [
        { code: 'DS', name: 'Lower Silesian' },
        { code: 'KP', name: 'Kuyavian-Pomeranian' },
        { code: 'LU', name: 'Lublin' },
        { code: 'LB', name: 'Lubusz' },
        { code: 'LD', name: 'Łódź' },
        { code: 'MA', name: 'Lesser Poland' },
        { code: 'MZ', name: 'Masovian' },
        { code: 'OP', name: 'Opole' },
        { code: 'PK', name: 'Subcarpathian' },
        { code: 'PD', name: 'Podlaskie' },
        { code: 'PM', name: 'Pomeranian' },
        { code: 'SL', name: 'Silesian' },
        { code: 'SK', name: 'Świętokrzyskie' },
        { code: 'WN', name: 'Warmian-Masurian' },
        { code: 'WP', name: 'Greater Poland' },
        { code: 'ZP', name: 'West Pomeranian' }
      ],
      czechRegions: [
        { code: 'PR', name: 'Prague' },
        { code: 'ST', name: 'Central Bohemian' },
        { code: 'JC', name: 'South Bohemian' },
        { code: 'PL', name: 'Plzeň' },
        { code: 'KA', name: 'Karlovy Vary' },
        { code: 'US', name: 'Ústí nad Labem' },
        { code: 'LI', name: 'Liberec' },
        { code: 'KR', name: 'Hradec Králové' },
        { code: 'PA', name: 'Pardubice' },
        { code: 'VY', name: 'Vysočina' },
        { code: 'JM', name: 'South Moravian' },
        { code: 'OL', name: 'Olomouc' },
        { code: 'ZL', name: 'Zlín' },
        { code: 'MS', name: 'Moravian-Silesian' }
      ],
      hungarianCounties: [
        { code: 'BU', name: 'Budapest' },
        { code: 'BK', name: 'Bács-Kiskun' },
        { code: 'BE', name: 'Békés' },
        { code: 'BA', name: 'Baranya' },
        { code: 'BZ', name: 'Borsod-Abaúj-Zemplén' },
        { code: 'CS', name: 'Csongrád' },
        { code: 'FE', name: 'Fejér' },
        { code: 'GS', name: 'Győr-Moson-Sopron' },
        { code: 'HB', name: 'Hajdú-Bihar' },
        { code: 'HE', name: 'Heves' },
        { code: 'JN', name: 'Jász-Nagykun-Szolnok' },
        { code: 'KE', name: 'Komárom-Esztergom' },
        { code: 'NO', name: 'Nógrád' },
        { code: 'PE', name: 'Pest' },
        { code: 'SO', name: 'Somogy' },
        { code: 'SZ', name: 'Szabolcs-Szatmár-Bereg' },
        { code: 'TO', name: 'Tolna' },
        { code: 'VA', name: 'Vas' },
        { code: 'VE', name: 'Veszprém' },
        { code: 'ZA', name: 'Zala' }
      ],
      romanianCounties: [
        { code: 'AB', name: 'Alba' },
        { code: 'AR', name: 'Arad' },
        { code: 'AG', name: 'Argeș' },
        { code: 'BC', name: 'Bacău' },
        { code: 'BH', name: 'Bihor' },
        { code: 'BN', name: 'Bistrița-Năsăud' },
        { code: 'BT', name: 'Botoșani' },
        { code: 'BR', name: 'Brăila' },
        { code: 'BV', name: 'Brașov' },
        { code: 'B', name: 'Bucharest' },
        { code: 'BZ', name: 'Buzău' },
        { code: 'CL', name: 'Călărași' },
        { code: 'CS', name: 'Caraș-Severin' },
        { code: 'CJ', name: 'Cluj' },
        { code: 'CT', name: 'Constanța' },
        { code: 'CV', name: 'Covasna' },
        { code: 'DB', name: 'Dâmbovița' },
        { code: 'DJ', name: 'Dolj' },
        { code: 'GL', name: 'Galați' },
        { code: 'GR', name: 'Giurgiu' },
        { code: 'GJ', name: 'Gorj' },
        { code: 'HR', name: 'Harghita' },
        { code: 'HD', name: 'Hunedoara' },
        { code: 'IL', name: 'Ialomița' },
        { code: 'IS', name: 'Iași' },
        { code: 'IF', name: 'Ilfov' },
        { code: 'MM', name: 'Maramureș' },
        { code: 'MH', name: 'Mehedinți' },
        { code: 'MS', name: 'Mureș' },
        { code: 'NT', name: 'Neamț' },
        { code: 'OT', name: 'Olt' },
        { code: 'PH', name: 'Prahova' },
        { code: 'SJ', name: 'Sălaj' },
        { code: 'SM', name: 'Satu Mare' },
        { code: 'SB', name: 'Sibiu' },
        { code: 'SV', name: 'Suceava' },
        { code: 'TR', name: 'Teleorman' },
        { code: 'TM', name: 'Timiș' },
        { code: 'TL', name: 'Tulcea' },
        { code: 'VL', name: 'Vâlcea' },
        { code: 'VS', name: 'Vaslui' },
        { code: 'VN', name: 'Vrancea' }
      ],
      bulgarianProvinces: [
        { code: 'BG', name: 'Blagoevgrad' },
        { code: 'BU', name: 'Burgas' },
        { code: 'DO', name: 'Dobrich' },
        { code: 'GA', name: 'Gabrovo' },
        { code: 'HA', name: 'Haskovo' },
        { code: 'KR', name: 'Kardzhali' },
        { code: 'KY', name: 'Kyustendil' },
        { code: 'LO', name: 'Lovech' },
        { code: 'MO', name: 'Montana' },
        { code: 'PA', name: 'Pazardzhik' },
        { code: 'PE', name: 'Pernik' },
        { code: 'PL', name: 'Pleven' },
        { code: 'PL', name: 'Plovdiv' },
        { code: 'RA', name: 'Razgrad' },
        { code: 'RU', name: 'Ruse' },
        { code: 'SH', name: 'Shumen' },
        { code: 'SI', name: 'Silistra' },
        { code: 'SL', name: 'Sliven' },
        { code: 'SM', name: 'Smolyan' },
        { code: 'SO', name: 'Sofia' },
        { code: 'SZ', name: 'Stara Zagora' },
        { code: 'TG', name: 'Targovishte' },
        { code: 'VA', name: 'Varna' },
        { code: 'VI', name: 'Veliko Tarnovo' },
        { code: 'VD', name: 'Vidin' },
        { code: 'VR', name: 'Vratsa' },
        { code: 'YA', name: 'Yambol' }
      ],
      croatianCounties: [
        { code: 'ZG', name: 'Zagreb' },
        { code: 'KA', name: 'Krapina-Zagorje' },
        { code: 'SI', name: 'Sisak-Moslavina' },
        { code: 'KK', name: 'Karlovac' },
        { code: 'VA', name: 'Varaždin' },
        { code: 'KO', name: 'Koprivnica-Križevci' },
        { code: 'BJ', name: 'Bjelovar-Bilogora' },
        { code: 'PR', name: 'Primorje-Gorski Kotar' },
        { code: 'LI', name: 'Lika-Senj' },
        { code: 'VI', name: 'Virovitica-Podravina' },
        { code: 'PO', name: 'Požega-Slavonia' },
        { code: 'BR', name: 'Brod-Posavina' },
        { code: 'ZD', name: 'Zadar' },
        { code: 'OS', name: 'Osijek-Baranja' },
        { code: 'ŠI', name: 'Šibenik-Knin' },
        { code: 'VS', name: 'Vukovar-Srijem' },
        { code: 'SP', name: 'Split-Dalmatia' },
        { code: 'IS', name: 'Istria' },
        { code: 'DU', name: 'Dubrovnik-Neretva' },
        { code: 'ME', name: 'Međimurje' },
        { code: 'ZG', name: 'Zagreb County' }
      ],
      irishCounties: [
        { code: 'D', name: 'Dublin' },
        { code: 'CW', name: 'Carlow' },
        { code: 'CN', name: 'Cavan' },
        { code: 'CE', name: 'Clare' },
        { code: 'CK', name: 'Cork' },
        { code: 'DL', name: 'Donegal' },
        { code: 'G', name: 'Galway' },
        { code: 'KY', name: 'Kerry' },
        { code: 'KE', name: 'Kildare' },
        { code: 'KK', name: 'Kilkenny' },
        { code: 'LS', name: 'Laois' },
        { code: 'LM', name: 'Leitrim' },
        { code: 'LK', name: 'Limerick' },
        { code: 'LD', name: 'Longford' },
        { code: 'LH', name: 'Louth' },
        { code: 'MO', name: 'Mayo' },
        { code: 'MH', name: 'Meath' },
        { code: 'MN', name: 'Monaghan' },
        { code: 'OY', name: 'Offaly' },
        { code: 'RN', name: 'Roscommon' },
        { code: 'SO', name: 'Sligo' },
        { code: 'TY', name: 'Tipperary' },
        { code: 'WD', name: 'Waterford' },
        { code: 'WH', name: 'Westmeath' },
        { code: 'WX', name: 'Wexford' },
        { code: 'WW', name: 'Wicklow' }
      ],
      icelandicRegions: [
        { code: 'HF', name: 'Capital Region' },
        { code: 'NE', name: 'Northeastern Region' },
        { code: 'NO', name: 'Northwestern Region' },
        { code: 'SU', name: 'Southern Peninsula' },
        { code: 'SO', name: 'Southern Region' },
        { code: 'WE', name: 'Western Region' },
        { code: 'WV', name: 'Westfjords' },
        { code: 'EA', name: 'Eastern Region' }
      ],
      luxembourgDistricts: [
        { code: 'L', name: 'Luxembourg' },
        { code: 'D', name: 'Diekirch' },
        { code: 'G', name: 'Grevenmacher' }
      ],
      malteseRegions: [
        { code: '01', name: 'Attard' },
        { code: '02', name: 'Balzan' },
        { code: '03', name: 'Birgu' },
        { code: '04', name: 'Birkirkara' },
        { code: '05', name: 'Birżebbuġa' },
        { code: '06', name: 'Bormla' },
        { code: '07', name: 'Dingli' },
        { code: '08', name: 'Fgura' },
        { code: '09', name: 'Floriana' },
        { code: '10', name: 'Fontana' },
        { code: '11', name: 'Gudja' },
        { code: '12', name: 'Gżira' },
        { code: '13', name: 'Għajnsielem' },
        { code: '14', name: 'Għarb' },
        { code: '15', name: 'Għargħur' },
        { code: '16', name: 'Għasri' },
        { code: '17', name: 'Għaxaq' },
        { code: '18', name: 'Ħamrun' },
        { code: '19', name: 'Iklin' },
        { code: '20', name: 'Isla' },
        { code: '21', name: 'Kalkara' },
        { code: '22', name: 'Kerċem' },
        { code: '23', name: 'Kirkop' },
        { code: '24', name: 'Lija' },
        { code: '25', name: 'Luqa' },
        { code: '26', name: 'Marsa' },
        { code: '27', name: 'Marsaskala' },
        { code: '28', name: 'Marsaxlokk' },
        { code: '29', name: 'Mdina' },
        { code: '30', name: 'Mellieħa' },
        { code: '31', name: 'Mġarr' },
        { code: '32', name: 'Mosta' },
        { code: '33', name: 'Mqabba' },
        { code: '34', name: 'Msida' },
        { code: '35', name: 'Mtarfa' },
        { code: '36', name: 'Munxar' },
        { code: '37', name: 'Nadur' },
        { code: '38', name: 'Naxxar' },
        { code: '39', name: 'Paola' },
        { code: '40', name: 'Pembroke' },
        { code: '41', name: 'Pietà' },
        { code: '42', name: 'Qala' },
        { code: '43', name: 'Qormi' },
        { code: '44', name: 'Qrendi' },
        { code: '45', name: 'Rabat' },
        { code: '46', name: 'Safi' },
        { code: '47', name: 'San Ġwann' },
        { code: '48', name: 'San Pawl il-Baħar' },
        { code: '49', name: 'Santa Luċija' },
        { code: '50', name: 'Santa Venera' },
        { code: '51', name: 'Senglea' },
        { code: '52', name: 'Siġġiewi' },
        { code: '53', name: 'Sliema' },
        { code: '54', name: 'St. Julian\'s' },
        { code: '55', name: 'St. Paul\'s Bay' },
        { code: '56', name: 'Swieqi' },
        { code: '57', name: 'Ta\' Xbiex' },
        { code: '58', name: 'Tarxien' },
        { code: '59', name: 'Valletta' },
        { code: '60', name: 'Xagħra' },
        { code: '61', name: 'Xewkija' },
        { code: '62', name: 'Xgħajra' },
        { code: '63', name: 'Żabbar' },
        { code: '64', name: 'Żebbuġ' },
        { code: '65', name: 'Żejtun' },
        { code: '66', name: 'Żurrieq' }
      ],
      slovakRegions: [
        { code: 'BC', name: 'Banská Bystrica' },
        { code: 'BL', name: 'Bratislava' },
        { code: 'KI', name: 'Košice' },
        { code: 'NI', name: 'Nitra' },
        { code: 'PV', name: 'Prešov' },
        { code: 'TC', name: 'Trenčín' },
        { code: 'TA', name: 'Trnava' },
        { code: 'ZI', name: 'Žilina' }
      ],
      slovenianRegions: [
        { code: '001', name: 'Ajdovščina' },
        { code: '002', name: 'Beltinci' },
        { code: '003', name: 'Bled' },
        { code: '004', name: 'Bohinj' },
        { code: '005', name: 'Borovnica' },
        { code: '006', name: 'Bovec' },
        { code: '007', name: 'Brda' },
        { code: '008', name: 'Brezovica' },
        { code: '009', name: 'Brežice' },
        { code: '010', name: 'Tišina' },
        { code: '011', name: 'Celje' },
        { code: '012', name: 'Cerklje na Gorenjskem' },
        { code: '013', name: 'Cerknica' },
        { code: '014', name: 'Cerkno' },
        { code: '015', name: 'Črenšovci' },
        { code: '016', name: 'Črna na Koroškem' },
        { code: '017', name: 'Črnomelj' },
        { code: '018', name: 'Destrnik' },
        { code: '019', name: 'Divača' },
        { code: '020', name: 'Dobrepolje' },
        { code: '021', name: 'Dobrova-Polhov Gradec' },
        { code: '022', name: 'Dol pri Ljubljani' },
        { code: '023', name: 'Domžale' },
        { code: '024', name: 'Dornava' },
        { code: '025', name: 'Dravograd' },
        { code: '026', name: 'Duplek' },
        { code: '027', name: 'Gorenja vas-Poljane' },
        { code: '028', name: 'Gorišnica' },
        { code: '029', name: 'Gornja Radgona' },
        { code: '030', name: 'Gornji Grad' },
        { code: '031', name: 'Gornji Petrovci' },
        { code: '032', name: 'Grosuplje' },
        { code: '033', name: 'Šalovci' },
        { code: '034', name: 'Hrastnik' },
        { code: '035', name: 'Hrpelje-Kozina' },
        { code: '036', name: 'Idrija' },
        { code: '037', name: 'Ig' },
        { code: '038', name: 'Ilirska Bistrica' },
        { code: '039', name: 'Ivančna Gorica' },
        { code: '040', name: 'Izola' },
        { code: '041', name: 'Jesenice' },
        { code: '042', name: 'Juršinci' },
        { code: '043', name: 'Kamnik' },
        { code: '044', name: 'Kanal' },
        { code: '045', name: 'Kidričevo' },
        { code: '046', name: 'Kobarid' },
        { code: '047', name: 'Kobilje' },
        { code: '048', name: 'Kočevje' },
        { code: '049', name: 'Komen' },
        { code: '050', name: 'Koper' },
        { code: '051', name: 'Kozje' },
        { code: '052', name: 'Kranj' },
        { code: '053', name: 'Kranjska Gora' },
        { code: '054', name: 'Krško' },
        { code: '055', name: 'Kungota' },
        { code: '056', name: 'Kuzma' },
        { code: '057', name: 'Laško' },
        { code: '058', name: 'Lenart' },
        { code: '059', name: 'Lendava' },
        { code: '060', name: 'Litija' },
        { code: '061', name: 'Ljubljana' },
        { code: '062', name: 'Ljubno' },
        { code: '063', name: 'Ljutomer' },
        { code: '064', name: 'Logatec' },
        { code: '065', name: 'Loška dolina' },
        { code: '066', name: 'Loški Potok' },
        { code: '067', name: 'Luče' },
        { code: '068', name: 'Lukovica' },
        { code: '069', name: 'Majšperk' },
        { code: '070', name: 'Maribor' },
        { code: '071', name: 'Medvode' },
        { code: '072', name: 'Mengeš' },
        { code: '073', name: 'Metlika' },
        { code: '074', name: 'Mežica' },
        { code: '075', name: 'Miren-Kostanjevica' },
        { code: '076', name: 'Mislinja' },
        { code: '077', name: 'Moravče' },
        { code: '078', name: 'Moravske Toplice' },
        { code: '079', name: 'Mozirje' },
        { code: '080', name: 'Murska Sobota' },
        { code: '081', name: 'Muta' },
        { code: '082', name: 'Naklo' },
        { code: '083', name: 'Nazarje' },
        { code: '084', name: 'Nova Gorica' },
        { code: '085', name: 'Novo Mesto' },
        { code: '086', name: 'Odranci' },
        { code: '087', name: 'Ormož' },
        { code: '088', name: 'Osilnica' },
        { code: '089', name: 'Pesnica' },
        { code: '090', name: 'Piran' },
        { code: '091', name: 'Pivka' },
        { code: '092', name: 'Podčetrtek' },
        { code: '093', name: 'Podvelka' },
        { code: '094', name: 'Postojna' },
        { code: '095', name: 'Preddvor' },
        { code: '096', name: 'Ptuj' },
        { code: '097', name: 'Puconci' },
        { code: '098', name: 'Rače-Fram' },
        { code: '099', name: 'Radeče' },
        { code: '100', name: 'Radenci' },
        { code: '101', name: 'Radlje ob Dravi' },
        { code: '102', name: 'Radovljica' },
        { code: '103', name: 'Ravne na Koroškem' },
        { code: '104', name: 'Ribnica' },
        { code: '105', name: 'Rogašovci' },
        { code: '106', name: 'Rogaška Slatina' },
        { code: '107', name: 'Rogatec' },
        { code: '108', name: 'Ruše' },
        { code: '109', name: 'Semič' },
        { code: '110', name: 'Sevnica' },
        { code: '111', name: 'Sežana' },
        { code: '112', name: 'Škocjan' },
        { code: '113', name: 'Škofja Loka' },
        { code: '114', name: 'Škofljica' },
        { code: '115', name: 'Slovenj Gradec' },
        { code: '116', name: 'Slovenska Bistrica' },
        { code: '117', name: 'Slovenske Konjice' },
        { code: '118', name: 'Starše' },
        { code: '119', name: 'Sveti Jurij' },
        { code: '120', name: 'Šenčur' },
        { code: '121', name: 'Šentilj' },
        { code: '122', name: 'Šentjernej' },
        { code: '123', name: 'Šentjur' },
        { code: '124', name: 'Škofja Loka' },
        { code: '125', name: 'Šmarje pri Jelšah' },
        { code: '126', name: 'Šmartno ob Paki' },
        { code: '127', name: 'Šoštanj' },
        { code: '128', name: 'Štore' },
        { code: '129', name: 'Tolmin' },
        { code: '130', name: 'Trbovlje' },
        { code: '131', name: 'Trebnje' },
        { code: '132', name: 'Tržič' },
        { code: '133', name: 'Turnišče' },
        { code: '134', name: 'Velenje' },
        { code: '135', name: 'Velike Lašče' },
        { code: '136', name: 'Videm' },
        { code: '137', name: 'Vipava' },
        { code: '138', name: 'Vitanje' },
        { code: '139', name: 'Vodice' },
        { code: '140', name: 'Vojnik' },
        { code: '141', name: 'Vrhnika' },
        { code: '142', name: 'Vuzenica' },
        { code: '143', name: 'Zagorje ob Savi' },
        { code: '144', name: 'Zavrč' },
        { code: '145', name: 'Zreče' },
        { code: '146', name: 'Železniki' },
        { code: '147', name: 'Žiri' },
        { code: '148', name: 'Benedikt' },
        { code: '149', name: 'Bistrica ob Sotli' },
        { code: '150', name: 'Bloke' },
        { code: '151', name: 'Braslovče' },
        { code: '152', name: 'Cankova' },
        { code: '153', name: 'Cerkvenjak' },
        { code: '154', name: 'Dobje' },
        { code: '155', name: 'Dobrna' },
        { code: '156', name: 'Dobrovnik' },
        { code: '157', name: 'Dolenjske Toplice' },
        { code: '158', name: 'Grad' },
        { code: '159', name: 'Hajdina' },
        { code: '160', name: 'Hoče-Slivnica' },
        { code: '161', name: 'Hodoš' },
        { code: '162', name: 'Horjul' },
        { code: '163', name: 'Jezersko' },
        { code: '164', name: 'Komenda' },
        { code: '165', name: 'Kostel' },
        { code: '166', name: 'Križevci' },
        { code: '167', name: 'Lovrenc na Pohorju' },
        { code: '168', name: 'Markovci' },
        { code: '169', name: 'Miklavž na Dravskem polju' },
        { code: '170', name: 'Mirna Peč' },
        { code: '171', name: 'Oplotnica' },
        { code: '172', name: 'Podlehnik' },
        { code: '173', name: 'Polzela' },
        { code: '174', name: 'Prebold' },
        { code: '175', name: 'Prevalje' },
        { code: '176', name: 'Razkrižje' },
        { code: '177', name: 'Ribnica na Pohorju' },
        { code: '178', name: 'Selnica ob Dravi' },
        { code: '179', name: 'Sodražica' },
        { code: '180', name: 'Solčava' },
        { code: '181', name: 'Sveta Ana' },
        { code: '182', name: 'Sveti Andraž v Slovenskih goricah' },
        { code: '183', name: 'Sveti Tomaž' },
        { code: '184', name: 'Šmarješke Toplice' },
        { code: '185', name: 'Tabor' },
        { code: '186', name: 'Trnovska vas' },
        { code: '187', name: 'Trzin' },
        { code: '188', name: 'Velika Polana' },
        { code: '189', name: 'Veržej' },
        { code: '190', name: 'Videm pri Ptuju' },
        { code: '191', name: 'Vojnik' },
        { code: '192', name: 'Vransko' },
        { code: '193', name: 'Žalec' },
        { code: '194', name: 'Žetale' },
        { code: '195', name: 'Žirovnica' },
        { code: '196', name: 'Žužemberk' },
        { code: '197', name: 'Šmartno pri Litiji' },
        { code: '198', name: 'Apače' },
        { code: '199', name: 'Cirkulane' },
        { code: '200', name: 'Kosanjevica na Krki' },
        { code: '201', name: 'Makole' },
        { code: '202', name: 'Mokronog-Trebelno' },
        { code: '203', name: 'Poljčane' },
        { code: '204', name: 'Renče-Vogrsko' },
        { code: '205', name: 'Središče ob Dravi' },
        { code: '206', name: 'Straža' },
        { code: '207', name: 'Sveta Trojica v Slovenskih goricah' },
        { code: '208', name: 'Sveti Tomaž' },
        { code: '209', name: 'Šentrupert' },
        { code: '210', name: 'Šmarješke Toplice' },
        { code: '211', name: 'Gorje' },
        { code: '212', name: 'Log-Dragomer' },
        { code: '213', name: 'Rečica ob Savinji' },
        { code: '214', name: 'Sveti Jurij v Slovenskih goricah' },
        { code: '215', name: 'Šentrupert' },
        { code: '216', name: 'Mirna' },
        { code: '217', name: 'Ankaran' }
      ],
      estonianCounties: [
        { code: '37', name: 'Harju County' },
        { code: '39', name: 'Hiiu County' },
        { code: '44', name: 'Ida-Viru County' },
        { code: '49', name: 'Jõgeva County' },
        { code: '51', name: 'Järva County' },
        { code: '57', name: 'Lääne County' },
        { code: '59', name: 'Lääne-Viru County' },
        { code: '65', name: 'Põlva County' },
        { code: '67', name: 'Pärnu County' },
        { code: '70', name: 'Rapla County' },
        { code: '74', name: 'Saare County' },
        { code: '78', name: 'Tartu County' },
        { code: '82', name: 'Valga County' },
        { code: '84', name: 'Viljandi County' },
        { code: '86', name: 'Võru County' }
      ],
      latvianRegions: [
        { code: '001', name: 'Aglona' },
        { code: '002', name: 'Aizkraukle' },
        { code: '003', name: 'Aizpute' },
        { code: '004', name: 'Aknīste' },
        { code: '005', name: 'Aloja' },
        { code: '006', name: 'Alsunga' },
        { code: '007', name: 'Alūksne' },
        { code: '008', name: 'Amatas' },
        { code: '009', name: 'Ape' },
        { code: '010', name: 'Auce' },
        { code: '011', name: 'Ādaži' },
        { code: '012', name: 'Babīte' },
        { code: '013', name: 'Baldone' },
        { code: '014', name: 'Baltinava' },
        { code: '015', name: 'Balvi' },
        { code: '016', name: 'Bauska' },
        { code: '017', name: 'Beverīna' },
        { code: '018', name: 'Brocēni' },
        { code: '019', name: 'Burtnieki' },
        { code: '020', name: 'Carnikava' },
        { code: '021', name: 'Cesvaine' },
        { code: '022', name: 'Cēsis' },
        { code: '023', name: 'Cibla' },
        { code: '024', name: 'Dagda' },
        { code: '025', name: 'Daugavpils' },
        { code: '026', name: 'Dobele' },
        { code: '027', name: 'Dundaga' },
        { code: '028', name: 'Durbe' },
        { code: '029', name: 'Engure' },
        { code: '030', name: 'Ērgļi' },
        { code: '031', name: 'Garkalne' },
        { code: '032', name: 'Grobiņa' },
        { code: '033', name: 'Gulbene' },
        { code: '034', name: 'Iecava' },
        { code: '035', name: 'Ikšķile' },
        { code: '036', name: 'Ilūkste' },
        { code: '037', name: 'Inčukalns' },
        { code: '038', name: 'Jaunjelgava' },
        { code: '039', name: 'Jaunpiebalga' },
        { code: '040', name: 'Jaunpils' },
        { code: '041', name: 'Jēkabpils' },
        { code: '042', name: 'Jelgava' },
        { code: '043', name: 'Jūrmala' },
        { code: '044', name: 'Kandava' },
        { code: '045', name: 'Kārsava' },
        { code: '046', name: 'Kocēni' },
        { code: '047', name: 'Koknese' },
        { code: '048', name: 'Krāslava' },
        { code: '049', name: 'Krimulda' },
        { code: '050', name: 'Krustpils' },
        { code: '051', name: 'Kuldīga' },
        { code: '052', name: 'Ķegums' },
        { code: '053', name: 'Ķekava' },
        { code: '054', name: 'Lielvārde' },
        { code: '055', name: 'Līgatne' },
        { code: '056', name: 'Limbaži' },
        { code: '057', name: 'Līvāni' },
        { code: '058', name: 'Lubāna' },
        { code: '059', name: 'Ludza' },
        { code: '060', name: 'Madona' },
        { code: '061', name: 'Mālpils' },
        { code: '062', name: 'Mārupe' },
        { code: '063', name: 'Mazsalaca' },
        { code: '064', name: 'Mērsrags' },
        { code: '065', name: 'Naukšēni' },
        { code: '066', name: 'Nereta' },
        { code: '067', name: 'Nīca' },
        { code: '068', name: 'Ogre' },
        { code: '069', name: 'Olaine' },
        { code: '070', name: 'Ozolnieki' },
        { code: '071', name: 'Pārgauja' },
        { code: '072', name: 'Pāvilosta' },
        { code: '073', name: 'Pļaviņas' },
        { code: '074', name: 'Preiļi' },
        { code: '075', name: 'Priekule' },
        { code: '076', name: 'Priekuļi' },
        { code: '077', name: 'Rauna' },
        { code: '078', name: 'Rēzekne' },
        { code: '079', name: 'Riebiņi' },
        { code: '080', name: 'Rīga' },
        { code: '081', name: 'Roja' },
        { code: '082', name: 'Ropaži' },
        { code: '083', name: 'Rucava' },
        { code: '084', name: 'Rugāji' },
        { code: '085', name: 'Rūjiena' },
        { code: '086', name: 'Rundāle' },
        { code: '087', name: 'Salacgrīva' },
        { code: '088', name: 'Sala' },
        { code: '089', name: 'Salaspils' },
        { code: '090', name: 'Saldus' },
        { code: '091', name: 'Saulkrasti' },
        { code: '092', name: 'Sēja' },
        { code: '093', name: 'Sigulda' },
        { code: '094', name: 'Skrīveri' },
        { code: '095', name: 'Skrunda' },
        { code: '096', name: 'Smiltene' },
        { code: '097', name: 'Stopiņi' },
        { code: '098', name: 'Strenči' },
        { code: '099', name: 'Talsi' },
        { code: '100', name: 'Tērvete' },
        { code: '101', name: 'Tukums' },
        { code: '102', name: 'Vaiņode' },
        { code: '103', name: 'Valka' },
        { code: '104', name: 'Valmiera' },
        { code: '105', name: 'Varakļāni' },
        { code: '106', name: 'Vārkava' },
        { code: '107', name: 'Vecpiebalga' },
        { code: '108', name: 'Vecumnieki' },
        { code: '109', name: 'Ventspils' },
        { code: '110', name: 'Viesīte' },
        { code: '111', name: 'Viļaka' },
        { code: '112', name: 'Viļāni' },
        { code: '113', name: 'Zilupe' }
      ],
      lithuanianCounties: [
        { code: 'AL', name: 'Alytus County' },
        { code: 'KU', name: 'Kaunas County' },
        { code: 'KL', name: 'Klaipėda County' },
        { code: 'MR', name: 'Marijampolė County' },
        { code: 'PN', name: 'Panevėžys County' },
        { code: 'ŠA', name: 'Šiauliai County' },
        { code: 'TA', name: 'Tauragė County' },
        { code: 'TE', name: 'Telšiai County' },
        { code: 'UT', name: 'Utena County' },
        { code: 'VL', name: 'Vilnius County' }
      ],
      russianRegions: [
        { code: 'AD', name: 'Adygea' },
        { code: 'AL', name: 'Altai Republic' },
        { code: 'BA', name: 'Bashkortostan' },
        { code: 'BU', name: 'Buryatia' },
        { code: 'CE', name: 'Chechnya' },
        { code: 'CU', name: 'Chuvashia' },
        { code: 'DA', name: 'Dagestan' },
        { code: 'IN', name: 'Ingushetia' },
        { code: 'KB', name: 'Kabardino-Balkaria' },
        { code: 'KL', name: 'Kalmykia' },
        { code: 'KC', name: 'Karachay-Cherkessia' },
        { code: 'KR', name: 'Karelia' },
        { code: 'KK', name: 'Khakassia' },
        { code: 'KO', name: 'Komi' },
        { code: 'ME', name: 'Mari El' },
        { code: 'MO', name: 'Mordovia' },
        { code: 'SA', name: 'Sakha' },
        { code: 'SE', name: 'North Ossetia-Alania' },
        { code: 'TA', name: 'Tatarstan' },
        { code: 'TY', name: 'Tuva' },
        { code: 'UD', name: 'Udmurtia' },
        { code: 'ALT', name: 'Altai Krai' },
        { code: 'KAM', name: 'Kamchatka Krai' },
        { code: 'KHA', name: 'Khabarovsk Krai' },
        { code: 'KRA', name: 'Krasnodar Krai' },
        { code: 'KRY', name: 'Krasnoyarsk Krai' },
        { code: 'PER', name: 'Perm Krai' },
        { code: 'PRI', name: 'Primorsky Krai' },
        { code: 'STA', name: 'Stavropol Krai' },
        { code: 'ZAB', name: 'Zabaykalsky Krai' },
        { code: 'AMU', name: 'Amur Oblast' },
        { code: 'ARK', name: 'Arkhangelsk Oblast' },
        { code: 'AST', name: 'Astrakhan Oblast' },
        { code: 'BEL', name: 'Belgorod Oblast' },
        { code: 'BRY', name: 'Bryansk Oblast' },
        { code: 'CHE', name: 'Chelyabinsk Oblast' },
        { code: 'IRK', name: 'Irkutsk Oblast' },
        { code: 'IVA', name: 'Ivanovo Oblast' },
        { code: 'KAL', name: 'Kaliningrad Oblast' },
        { code: 'KALU', name: 'Kaluga Oblast' },
        { code: 'KEM', name: 'Kemerovo Oblast' },
        { code: 'KIR', name: 'Kirov Oblast' },
        { code: 'KOS', name: 'Kostroma Oblast' },
        { code: 'KUR', name: 'Kurgan Oblast' },
        { code: 'KURS', name: 'Kursk Oblast' },
        { code: 'LEN', name: 'Leningrad Oblast' },
        { code: 'LIP', name: 'Lipetsk Oblast' },
        { code: 'MAG', name: 'Magadan Oblast' },
        { code: 'MOS', name: 'Moscow Oblast' },
        { code: 'MUR', name: 'Murmansk Oblast' },
        { code: 'NIZ', name: 'Nizhny Novgorod Oblast' },
        { code: 'NOV', name: 'Novgorod Oblast' },
        { code: 'NVS', name: 'Novosibirsk Oblast' },
        { code: 'OMS', name: 'Omsk Oblast' },
        { code: 'ORE', name: 'Orenburg Oblast' },
        { code: 'ORL', name: 'Oryol Oblast' },
        { code: 'PNZ', name: 'Penza Oblast' },
        { code: 'PSK', name: 'Pskov Oblast' },
        { code: 'ROS', name: 'Rostov Oblast' },
        { code: 'RYA', name: 'Ryazan Oblast' },
        { code: 'SAK', name: 'Sakhalin Oblast' },
        { code: 'SAM', name: 'Samara Oblast' },
        { code: 'SAR', name: 'Saratov Oblast' },
        { code: 'SMO', name: 'Smolensk Oblast' },
        { code: 'SVE', name: 'Sverdlovsk Oblast' },
        { code: 'TAM', name: 'Tambov Oblast' },
        { code: 'TOM', name: 'Tomsk Oblast' },
        { code: 'TUL', name: 'Tula Oblast' },
        { code: 'TVE', name: 'Tver Oblast' },
        { code: 'TYU', name: 'Tyumen Oblast' },
        { code: 'ULY', name: 'Ulyanovsk Oblast' },
        { code: 'VLA', name: 'Vladimir Oblast' },
        { code: 'VGG', name: 'Volgograd Oblast' },
        { code: 'VLG', name: 'Vologda Oblast' },
        { code: 'VOR', name: 'Voronezh Oblast' },
        { code: 'YAR', name: 'Yaroslavl Oblast' },
        { code: 'MOW', name: 'Moscow' },
        { code: 'SPE', name: 'Saint Petersburg' },
        { code: 'SEV', name: 'Sevastopol' },
        { code: 'YEV', name: 'Jewish Autonomous Oblast' },
        { code: 'NEN', name: 'Nenets Autonomous Okrug' },
        { code: 'KHM', name: 'Khanty-Mansi Autonomous Okrug' },
        { code: 'CHU', name: 'Chukotka Autonomous Okrug' },
        { code: 'YAN', name: 'Yamalo-Nenets Autonomous Okrug' }
      ],
      ukrainianOblasts: [
        { code: '05', name: 'Vinnytsia Oblast' },
        { code: '07', name: 'Volyn Oblast' },
        { code: '09', name: 'Luhansk Oblast' },
        { code: '12', name: 'Dnipropetrovsk Oblast' },
        { code: '14', name: 'Donetsk Oblast' },
        { code: '18', name: 'Zhytomyr Oblast' },
        { code: '21', name: 'Zakarpattia Oblast' },
        { code: '23', name: 'Zaporizhzhia Oblast' },
        { code: '26', name: 'Ivano-Frankivsk Oblast' },
        { code: '32', name: 'Kyiv Oblast' },
        { code: '35', name: 'Kirovohrad Oblast' },
        { code: '43', name: 'Lviv Oblast' },
        { code: '46', name: 'Mykolaiv Oblast' },
        { code: '48', name: 'Odessa Oblast' },
        { code: '51', name: 'Poltava Oblast' },
        { code: '53', name: 'Rivne Oblast' },
        { code: '56', name: 'Sumy Oblast' },
        { code: '59', name: 'Ternopil Oblast' },
        { code: '61', name: 'Kharkiv Oblast' },
        { code: '63', name: 'Kherson Oblast' },
        { code: '65', name: 'Khmelnytskyi Oblast' },
        { code: '68', name: 'Cherkasy Oblast' },
        { code: '71', name: 'Chernihiv Oblast' },
        { code: '74', name: 'Chernivtsi Oblast' },
        { code: '77', name: 'Crimea' },
        { code: '30', name: 'Kyiv' },
        { code: '40', name: 'Sevastopol' }
      ],
      turkishProvinces: [
        { code: '01', name: 'Adana' },
        { code: '02', name: 'Adıyaman' },
        { code: '03', name: 'Afyonkarahisar' },
        { code: '04', name: 'Ağrı' },
        { code: '05', name: 'Amasya' },
        { code: '06', name: 'Ankara' },
        { code: '07', name: 'Antalya' },
        { code: '08', name: 'Artvin' },
        { code: '09', name: 'Aydın' },
        { code: '10', name: 'Balıkesir' },
        { code: '11', name: 'Bilecik' },
        { code: '12', name: 'Bingöl' },
        { code: '13', name: 'Bitlis' },
        { code: '14', name: 'Bolu' },
        { code: '15', name: 'Burdur' },
        { code: '16', name: 'Bursa' },
        { code: '17', name: 'Çanakkale' },
        { code: '18', name: 'Çankırı' },
        { code: '19', name: 'Çorum' },
        { code: '20', name: 'Denizli' },
        { code: '21', name: 'Diyarbakır' },
        { code: '22', name: 'Edirne' },
        { code: '23', name: 'Elazığ' },
        { code: '24', name: 'Erzincan' },
        { code: '25', name: 'Erzurum' },
        { code: '26', name: 'Eskişehir' },
        { code: '27', name: 'Gaziantep' },
        { code: '28', name: 'Giresun' },
        { code: '29', name: 'Gümüşhane' },
        { code: '30', name: 'Hakkâri' },
        { code: '31', name: 'Hatay' },
        { code: '32', name: 'Isparta' },
        { code: '33', name: 'Mersin' },
        { code: '34', name: 'İstanbul' },
        { code: '35', name: 'İzmir' },
        { code: '36', name: 'Kars' },
        { code: '37', name: 'Kastamonu' },
        { code: '38', name: 'Kayseri' },
        { code: '39', name: 'Kırklareli' },
        { code: '40', name: 'Kırşehir' },
        { code: '41', name: 'Kocaeli' },
        { code: '42', name: 'Konya' },
        { code: '43', name: 'Kütahya' },
        { code: '44', name: 'Malatya' },
        { code: '45', name: 'Manisa' },
        { code: '46', name: 'Kahramanmaraş' },
        { code: '47', name: 'Mardin' },
        { code: '48', name: 'Muğla' },
        { code: '49', name: 'Muş' },
        { code: '50', name: 'Nevşehir' },
        { code: '51', name: 'Niğde' },
        { code: '52', name: 'Ordu' },
        { code: '53', name: 'Rize' },
        { code: '54', name: 'Sakarya' },
        { code: '55', name: 'Samsun' },
        { code: '56', name: 'Siirt' },
        { code: '57', name: 'Sinop' },
        { code: '58', name: 'Sivas' },
        { code: '59', name: 'Tekirdağ' },
        { code: '60', name: 'Tokat' },
        { code: '61', name: 'Trabzon' },
        { code: '62', name: 'Tunceli' },
        { code: '63', name: 'Şanlıurfa' },
        { code: '64', name: 'Uşak' },
        { code: '65', name: 'Van' },
        { code: '66', name: 'Yozgat' },
        { code: '67', name: 'Zonguldak' },
        { code: '68', name: 'Aksaray' },
        { code: '69', name: 'Bayburt' },
        { code: '70', name: 'Karaman' },
        { code: '71', name: 'Kırıkkale' },
        { code: '72', name: 'Batman' },
        { code: '73', name: 'Şırnak' },
        { code: '74', name: 'Bartın' },
        { code: '75', name: 'Ardahan' },
        { code: '76', name: 'Iğdır' },
        { code: '77', name: 'Yalova' },
        { code: '78', name: 'Karabük' },
        { code: '79', name: 'Kilis' },
        { code: '80', name: 'Osmaniye' },
        { code: '81', name: 'Düzce' }
      ],
      saudiProvinces: [
        { code: '01', name: 'Al Bahah' },
        { code: '02', name: 'Northern Borders' },
        { code: '03', name: 'Al Jawf' },
        { code: '04', name: 'Al Madinah' },
        { code: '05', name: 'Al Qassim' },
        { code: '06', name: 'Ar Riyad' },
        { code: '07', name: 'Eastern Province' },
        { code: '08', name: 'Asir' },
        { code: '09', name: 'Hail' },
        { code: '10', name: 'Jazan' },
        { code: '11', name: 'Makkah' },
        { code: '12', name: 'Najran' },
        { code: '13', name: 'Tabuk' }
      ],
      uaeEmirates: [
        { code: 'AZ', name: 'Abu Dhabi' },
        { code: 'AJ', name: 'Ajman' },
        { code: 'FU', name: 'Fujairah' },
        { code: 'SH', name: 'Sharjah' },
        { code: 'DU', name: 'Dubai' },
        { code: 'RK', name: 'Ras Al Khaimah' },
        { code: 'UQ', name: 'Umm Al Quwain' }
      ],
      israeliDistricts: [
        { code: 'IL', name: 'Jerusalem' },
        { code: 'TA', name: 'Tel Aviv' },
        { code: 'HA', name: 'Haifa' },
        { code: 'CS', name: 'Central' },
        { code: 'SM', name: 'Southern' },
        { code: 'NZ', name: 'Northern' }
      ],
      southAfricanProvinces: [
        { code: 'EC', name: 'Eastern Cape' },
        { code: 'FS', name: 'Free State' },
        { code: 'GP', name: 'Gauteng' },
        { code: 'KZN', name: 'KwaZulu-Natal' },
        { code: 'LP', name: 'Limpopo' },
        { code: 'MP', name: 'Mpumalanga' },
        { code: 'NC', name: 'Northern Cape' },
        { code: 'NW', name: 'North West' },
        { code: 'WC', name: 'Western Cape' }
      ],
      nigerianStates: [
        { code: 'AB', name: 'Abia' },
        { code: 'AD', name: 'Adamawa' },
        { code: 'AK', name: 'Akwa Ibom' },
        { code: 'AN', name: 'Anambra' },
        { code: 'BA', name: 'Bauchi' },
        { code: 'BY', name: 'Bayelsa' },
        { code: 'BE', name: 'Benue' },
        { code: 'BO', name: 'Borno' },
        { code: 'CR', name: 'Cross River' },
        { code: 'DE', name: 'Delta' },
        { code: 'EB', name: 'Ebonyi' },
        { code: 'ED', name: 'Edo' },
        { code: 'EK', name: 'Ekiti' },
        { code: 'EN', name: 'Enugu' },
        { code: 'GO', name: 'Gombe' },
        { code: 'IM', name: 'Imo' },
        { code: 'JI', name: 'Jigawa' },
        { code: 'KD', name: 'Kaduna' },
        { code: 'KN', name: 'Kano' },
        { code: 'KT', name: 'Katsina' },
        { code: 'KE', name: 'Kebbi' },
        { code: 'KO', name: 'Kogi' },
        { code: 'KW', name: 'Kwara' },
        { code: 'LA', name: 'Lagos' },
        { code: 'NA', name: 'Nasarawa' },
        { code: 'NI', name: 'Niger' },
        { code: 'OG', name: 'Ogun' },
        { code: 'ON', name: 'Ondo' },
        { code: 'OS', name: 'Osun' },
        { code: 'OY', name: 'Oyo' },
        { code: 'PL', name: 'Plateau' },
        { code: 'RI', name: 'Rivers' },
        { code: 'SO', name: 'Sokoto' },
        { code: 'TA', name: 'Taraba' },
        { code: 'YO', name: 'Yobe' },
        { code: 'ZA', name: 'Zamfara' },
        { code: 'FC', name: 'FCT Abuja' }
      ],
      kenyanCounties: [
        { code: '001', name: 'Baringo' },
        { code: '002', name: 'Bomet' },
        { code: '003', name: 'Bungoma' },
        { code: '004', name: 'Busia' },
        { code: '005', name: 'Elgeyo-Marakwet' },
        { code: '006', name: 'Embu' },
        { code: '007', name: 'Garissa' },
        { code: '008', name: 'Homa Bay' },
        { code: '009', name: 'Isiolo' },
        { code: '010', name: 'Kajiado' },
        { code: '011', name: 'Kakamega' },
        { code: '012', name: 'Kericho' },
        { code: '013', name: 'Kiambu' },
        { code: '014', name: 'Kilifi' },
        { code: '015', name: 'Kirinyaga' },
        { code: '016', name: 'Kisii' },
        { code: '017', name: 'Kisumu' },
        { code: '018', name: 'Kitui' },
        { code: '019', name: 'Kwale' },
        { code: '020', name: 'Laikipia' },
        { code: '021', name: 'Lamu' },
        { code: '022', name: 'Machakos' },
        { code: '023', name: 'Makueni' },
        { code: '024', name: 'Mandera' },
        { code: '025', name: 'Marsabit' },
        { code: '026', name: 'Meru' },
        { code: '027', name: 'Migori' },
        { code: '028', name: 'Mombasa' },
        { code: '029', name: 'Murang\'a' },
        { code: '030', name: 'Nairobi' },
        { code: '031', name: 'Nakuru' },
        { code: '032', name: 'Nandi' },
        { code: '033', name: 'Narok' },
        { code: '034', name: 'Nyamira' },
        { code: '035', name: 'Nyandarua' },
        { code: '036', name: 'Nyeri' },
        { code: '037', name: 'Samburu' },
        { code: '038', name: 'Siaya' },
        { code: '039', name: 'Taita-Taveta' },
        { code: '040', name: 'Tana River' },
        { code: '041', name: 'Tharaka-Nithi' },
        { code: '042', name: 'Trans Nzoia' },
        { code: '043', name: 'Turkana' },
        { code: '044', name: 'Uasin Gishu' },
        { code: '045', name: 'Vihiga' },
        { code: '046', name: 'Wajir' },
        { code: '047', name: 'West Pokot' }
      ],
      argentineProvinces: [
        { code: 'C', name: 'Ciudad Autónoma de Buenos Aires' },
        { code: 'B', name: 'Buenos Aires' },
        { code: 'K', name: 'Catamarca' },
        { code: 'H', name: 'Chaco' },
        { code: 'U', name: 'Chubut' },
        { code: 'X', name: 'Córdoba' },
        { code: 'W', name: 'Corrientes' },
        { code: 'E', name: 'Entre Ríos' },
        { code: 'P', name: 'Formosa' },
        { code: 'Y', name: 'Jujuy' },
        { code: 'L', name: 'La Pampa' },
        { code: 'F', name: 'La Rioja' },
        { code: 'M', name: 'Mendoza' },
        { code: 'N', name: 'Misiones' },
        { code: 'Q', name: 'Neuquén' },
        { code: 'R', name: 'Río Negro' },
        { code: 'A', name: 'Salta' },
        { code: 'J', name: 'San Juan' },
        { code: 'D', name: 'San Luis' },
        { code: 'Z', name: 'Santa Cruz' },
        { code: 'S', name: 'Santa Fe' },
        { code: 'G', name: 'Santiago del Estero' },
        { code: 'V', name: 'Tierra del Fuego' },
        { code: 'T', name: 'Tucumán' }
      ],
      chileanRegions: [
        { code: 'XV', name: 'Arica y Parinacota' },
        { code: 'I', name: 'Tarapacá' },
        { code: 'II', name: 'Antofagasta' },
        { code: 'III', name: 'Atacama' },
        { code: 'IV', name: 'Coquimbo' },
        { code: 'V', name: 'Valparaíso' },
        { code: 'VI', name: 'O\'Higgins' },
        { code: 'VII', name: 'Maule' },
        { code: 'VIII', name: 'Biobío' },
        { code: 'IX', name: 'Araucanía' },
        { code: 'XIV', name: 'Los Ríos' },
        { code: 'X', name: 'Los Lagos' },
        { code: 'XI', name: 'Aysén' },
        { code: 'XII', name: 'Magallanes' },
        { code: 'XIII', name: 'Metropolitana' },
        { code: 'XVI', name: 'Ñuble' }
      ],
      colombianDepartments: [
        { code: 'AMA', name: 'Amazonas' },
        { code: 'ANT', name: 'Antioquia' },
        { code: 'ARA', name: 'Arauca' },
        { code: 'ATL', name: 'Atlántico' },
        { code: 'BOL', name: 'Bolívar' },
        { code: 'BOY', name: 'Boyacá' },
        { code: 'CAL', name: 'Caldas' },
        { code: 'CAQ', name: 'Caquetá' },
        { code: 'CAS', name: 'Casanare' },
        { code: 'CAU', name: 'Cauca' },
        { code: 'CES', name: 'Cesar' },
        { code: 'CHO', name: 'Chocó' },
        { code: 'COR', name: 'Córdoba' },
        { code: 'CUN', name: 'Cundinamarca' },
        { code: 'GUA', name: 'Guainía' },
        { code: 'GUV', name: 'Guaviare' },
        { code: 'HUI', name: 'Huila' },
        { code: 'LAG', name: 'La Guajira' },
        { code: 'MAG', name: 'Magdalena' },
        { code: 'MET', name: 'Meta' },
        { code: 'NAR', name: 'Nariño' },
        { code: 'NSA', name: 'Norte de Santander' },
        { code: 'PUT', name: 'Putumayo' },
        { code: 'QUI', name: 'Quindío' },
        { code: 'RIS', name: 'Risaralda' },
        { code: 'SAP', name: 'San Andrés y Providencia' },
        { code: 'SAN', name: 'Santander' },
        { code: 'SUC', name: 'Sucre' },
        { code: 'TOL', name: 'Tolima' },
        { code: 'VAC', name: 'Valle del Cauca' },
        { code: 'VAU', name: 'Vaupés' },
        { code: 'VID', name: 'Vichada' },
        { code: 'DC', name: 'Bogotá D.C.' }
      ],
      peruvianRegions: [
        { code: 'AMA', name: 'Amazonas' },
        { code: 'ANC', name: 'Ancash' },
        { code: 'APU', name: 'Apurímac' },
        { code: 'ARE', name: 'Arequipa' },
        { code: 'AYA', name: 'Ayacucho' },
        { code: 'CAJ', name: 'Cajamarca' },
        { code: 'CAL', name: 'Callao' },
        { code: 'CUS', name: 'Cusco' },
        { code: 'HUV', name: 'Huancavelica' },
        { code: 'HUC', name: 'Huánuco' },
        { code: 'ICA', name: 'Ica' },
        { code: 'JUN', name: 'Junín' },
        { code: 'LAL', name: 'La Libertad' },
        { code: 'LAM', name: 'Lambayeque' },
        { code: 'LIM', name: 'Lima' },
        { code: 'LOR', name: 'Loreto' },
        { code: 'MDD', name: 'Madre de Dios' },
        { code: 'MOQ', name: 'Moquegua' },
        { code: 'PAS', name: 'Pasco' },
        { code: 'PIU', name: 'Piura' },
        { code: 'PUN', name: 'Puno' },
        { code: 'SAM', name: 'San Martín' },
        { code: 'TAC', name: 'Tacna' },
        { code: 'TUM', name: 'Tumbes' },
        { code: 'UCA', name: 'Ucayali' }
      ],
      venezuelanStates: [
        { code: 'A', name: 'Amazonas' },
        { code: 'B', name: 'Anzoátegui' },
        { code: 'C', name: 'Apure' },
        { code: 'D', name: 'Aragua' },
        { code: 'E', name: 'Barinas' },
        { code: 'F', name: 'Bolívar' },
        { code: 'G', name: 'Carabobo' },
        { code: 'H', name: 'Cojedes' },
        { code: 'I', name: 'Delta Amacuro' },
        { code: 'J', name: 'Falcón' },
        { code: 'K', name: 'Guárico' },
        { code: 'L', name: 'Lara' },
        { code: 'M', name: 'Mérida' },
        { code: 'N', name: 'Miranda' },
        { code: 'O', name: 'Monagas' },
        { code: 'P', name: 'Nueva Esparta' },
        { code: 'Q', name: 'Portuguesa' },
        { code: 'R', name: 'Sucre' },
        { code: 'S', name: 'Táchira' },
        { code: 'T', name: 'Trujillo' },
        { code: 'U', name: 'Vargas' },
        { code: 'V', name: 'Yaracuy' },
        { code: 'W', name: 'Zulia' },
        { code: 'X', name: 'Distrito Capital' }
      ],
      ecuadorianProvinces: [
        { code: 'A', name: 'Azuay' },
        { code: 'B', name: 'Bolívar' },
        { code: 'C', name: 'Carchi' },
        { code: 'D', name: 'Cañar' },
        { code: 'E', name: 'Chimborazo' },
        { code: 'F', name: 'Cotopaxi' },
        { code: 'G', name: 'El Oro' },
        { code: 'H', name: 'Esmeraldas' },
        { code: 'I', name: 'Galápagos' },
        { code: 'J', name: 'Guayas' },
        { code: 'L', name: 'Loja' },
        { code: 'M', name: 'Los Ríos' },
        { code: 'N', name: 'Manabí' },
        { code: 'O', name: 'Morona-Santiago' },
        { code: 'P', name: 'Napo' },
        { code: 'Q', name: 'Pastaza' },
        { code: 'R', name: 'Pichincha' },
        { code: 'S', name: 'Tungurahua' },
        { code: 'T', name: 'Zamora-Chinchipe' },
        { code: 'U', name: 'Sucumbíos' },
        { code: 'W', name: 'Orellana' },
        { code: 'X', name: 'Santo Domingo de los Tsáchilas' },
        { code: 'Y', name: 'Santa Elena' },
        { code: 'Z', name: 'Imbabura' }
      ],
      uruguayanDepartments: [
        { code: 'AR', name: 'Artigas' },
        { code: 'CA', name: 'Canelones' },
        { code: 'CL', name: 'Cerro Largo' },
        { code: 'CO', name: 'Colonia' },
        { code: 'DU', name: 'Durazno' },
        { code: 'FS', name: 'Flores' },
        { code: 'FD', name: 'Florida' },
        { code: 'LA', name: 'Lavalleja' },
        { code: 'MA', name: 'Maldonado' },
        { code: 'MO', name: 'Montevideo' },
        { code: 'PA', name: 'Paysandú' },
        { code: 'RN', name: 'Río Negro' },
        { code: 'RV', name: 'Rivera' },
        { code: 'RO', name: 'Rocha' },
        { code: 'SA', name: 'Salto' },
        { code: 'SJ', name: 'San José' },
        { code: 'SO', name: 'Soriano' },
        { code: 'TA', name: 'Tacuarembó' },
        { code: 'TT', name: 'Treinta y Tres' }
      ],
      paraguayanDepartments: [
        { code: 'ASU', name: 'Asunción' },
        { code: '1', name: 'Concepción' },
        { code: '2', name: 'San Pedro' },
        { code: '3', name: 'Cordillera' },
        { code: '4', name: 'Guairá' },
        { code: '5', name: 'Caaguazú' },
        { code: '6', name: 'Caazapá' },
        { code: '7', name: 'Itapúa' },
        { code: '8', name: 'Misiones' },
        { code: '9', name: 'Paraguarí' },
        { code: '10', name: 'Alto Paraná' },
        { code: '11', name: 'Central' },
        { code: '12', name: 'Ñeembucú' },
        { code: '13', name: 'Amambay' },
        { code: '14', name: 'Canindeyú' },
        { code: '15', name: 'Presidente Hayes' },
        { code: '16', name: 'Alto Paraguay' },
        { code: '17', name: 'Boquerón' }
      ],
      bolivianDepartments: [
        { code: 'B', name: 'Beni' },
        { code: 'H', name: 'Chuquisaca' },
        { code: 'C', name: 'Cochabamba' },
        { code: 'L', name: 'La Paz' },
        { code: 'O', name: 'Oruro' },
        { code: 'N', name: 'Pando' },
        { code: 'P', name: 'Potosí' },
        { code: 'S', name: 'Santa Cruz' },
        { code: 'T', name: 'Tarija' }
      ],
      southKoreanProvinces: [
        { code: '11', name: 'Seoul' },
        { code: '26', name: 'Busan' },
        { code: '27', name: 'Daegu' },
        { code: '28', name: 'Incheon' },
        { code: '29', name: 'Gwangju' },
        { code: '30', name: 'Daejeon' },
        { code: '31', name: 'Ulsan' },
        { code: '41', name: 'Gyeonggi' },
        { code: '42', name: 'Gangwon' },
        { code: '43', name: 'North Chungcheong' },
        { code: '44', name: 'South Chungcheong' },
        { code: '45', name: 'North Jeolla' },
        { code: '46', name: 'South Jeolla' },
        { code: '47', name: 'North Gyeongsang' },
        { code: '48', name: 'South Gyeongsang' },
        { code: '49', name: 'Jeju' },
        { code: '50', name: 'Sejong' }
      ],
      thaiProvinces: [
        { code: '10', name: 'Bangkok' },
        { code: '11', name: 'Samut Prakan' },
        { code: '12', name: 'Nonthaburi' },
        { code: '13', name: 'Pathum Thani' },
        { code: '14', name: 'Phra Nakhon Si Ayutthaya' },
        { code: '15', name: 'Ang Thong' },
        { code: '16', name: 'Lopburi' },
        { code: '17', name: 'Sing Buri' },
        { code: '18', name: 'Chai Nat' },
        { code: '19', name: 'Saraburi' },
        { code: '20', name: 'Chonburi' },
        { code: '21', name: 'Rayong' },
        { code: '22', name: 'Chanthaburi' },
        { code: '23', name: 'Trat' },
        { code: '24', name: 'Chachoengsao' },
        { code: '25', name: 'Prachin Buri' },
        { code: '26', name: 'Nakhon Nayok' },
        { code: '27', name: 'Sa Kaeo' },
        { code: '30', name: 'Nakhon Ratchasima' },
        { code: '31', name: 'Buri Ram' },
        { code: '32', name: 'Surin' },
        { code: '33', name: 'Si Sa Ket' },
        { code: '34', name: 'Ubon Ratchathani' },
        { code: '35', name: 'Yasothon' },
        { code: '36', name: 'Chaiyaphum' },
        { code: '37', name: 'Amnat Charoen' },
        { code: '38', name: 'Bueng Kan' },
        { code: '39', name: 'Nong Bua Lamphu' },
        { code: '40', name: 'Khon Kaen' },
        { code: '41', name: 'Udon Thani' },
        { code: '42', name: 'Loei' },
        { code: '43', name: 'Nong Khai' },
        { code: '44', name: 'Maha Sarakham' },
        { code: '45', name: 'Roi Et' },
        { code: '46', name: 'Kalasin' },
        { code: '47', name: 'Sakon Nakhon' },
        { code: '48', name: 'Nakhon Phanom' },
        { code: '49', name: 'Mukdahan' },
        { code: '50', name: 'Chiang Mai' },
        { code: '51', name: 'Lamphun' },
        { code: '52', name: 'Lampang' },
        { code: '53', name: 'Uttaradit' },
        { code: '54', name: 'Phrae' },
        { code: '55', name: 'Nan' },
        { code: '56', name: 'Phayao' },
        { code: '57', name: 'Chiang Rai' },
        { code: '58', name: 'Mae Hong Son' },
        { code: '60', name: 'Nakhon Sawan' },
        { code: '61', name: 'Uthai Thani' },
        { code: '62', name: 'Kamphaeng Phet' },
        { code: '63', name: 'Tak' },
        { code: '64', name: 'Sukhothai' },
        { code: '65', name: 'Phitsanulok' },
        { code: '66', name: 'Phichit' },
        { code: '67', name: 'Phetchabun' },
        { code: '70', name: 'Ratchaburi' },
        { code: '71', name: 'Kanchanaburi' },
        { code: '72', name: 'Suphan Buri' },
        { code: '73', name: 'Nakhon Pathom' },
        { code: '74', name: 'Samut Sakhon' },
        { code: '75', name: 'Samut Songkhram' },
        { code: '76', name: 'Phetchaburi' },
        { code: '77', name: 'Prachuap Khiri Khan' },
        { code: '80', name: 'Nakhon Si Thammarat' },
        { code: '81', name: 'Krabi' },
        { code: '82', name: 'Phang Nga' },
        { code: '83', name: 'Phuket' },
        { code: '84', name: 'Surat Thani' },
        { code: '85', name: 'Ranong' },
        { code: '86', name: 'Chumphon' },
        { code: '90', name: 'Songkhla' },
        { code: '91', name: 'Satun' },
        { code: '92', name: 'Trang' },
        { code: '93', name: 'Phatthalung' },
        { code: '94', name: 'Pattani' },
        { code: '95', name: 'Yala' },
        { code: '96', name: 'Narathiwat' }
      ],
      vietnameseProvinces: [
        { code: '01', name: 'Lai Châu' },
        { code: '02', name: 'Lào Cai' },
        { code: '03', name: 'Hà Giang' },
        { code: '04', name: 'Cao Bằng' },
        { code: '05', name: 'Sơn La' },
        { code: '06', name: 'Yên Bái' },
        { code: '07', name: 'Tuyên Quang' },
        { code: '08', name: 'Lạng Sơn' },
        { code: '09', name: 'Quảng Ninh' },
        { code: '10', name: 'Hòa Bình' },
        { code: '11', name: 'Hà Nội' },
        { code: '12', name: 'Hải Phòng' },
        { code: '13', name: 'Hải Dương' },
        { code: '14', name: 'Hưng Yên' },
        { code: '15', name: 'Thái Bình' },
        { code: '16', name: 'Hà Nam' },
        { code: '17', name: 'Nam Định' },
        { code: '18', name: 'Ninh Bình' },
        { code: '19', name: 'Thanh Hóa' },
        { code: '20', name: 'Nghệ An' },
        { code: '21', name: 'Hà Tĩnh' },
        { code: '22', name: 'Quảng Bình' },
        { code: '23', name: 'Quảng Trị' },
        { code: '24', name: 'Thừa Thiên Huế' },
        { code: '25', name: 'Đà Nẵng' },
        { code: '26', name: 'Quảng Nam' },
        { code: '27', name: 'Quảng Ngãi' },
        { code: '28', name: 'Bình Định' },
        { code: '29', name: 'Phú Yên' },
        { code: '30', name: 'Khánh Hòa' },
        { code: '31', name: 'Ninh Thuận' },
        { code: '32', name: 'Bình Thuận' },
        { code: '33', name: 'Kon Tum' },
        { code: '34', name: 'Gia Lai' },
        { code: '35', name: 'Đắk Lắk' },
        { code: '36', name: 'Đắk Nông' },
        { code: '37', name: 'Lâm Đồng' },
        { code: '38', name: 'Bình Phước' },
        { code: '39', name: 'Tây Ninh' },
        { code: '40', name: 'Bình Dương' },
        { code: '41', name: 'Đồng Nai' },
        { code: '42', name: 'Bà Rịa - Vũng Tàu' },
        { code: '43', name: 'Hồ Chí Minh' },
        { code: '44', name: 'Long An' },
        { code: '45', name: 'Tiền Giang' },
        { code: '46', name: 'Bến Tre' },
        { code: '47', name: 'Trà Vinh' },
        { code: '48', name: 'Vĩnh Long' },
        { code: '49', name: 'Đồng Tháp' },
        { code: '50', name: 'An Giang' },
        { code: '51', name: 'Kiên Giang' },
        { code: '52', name: 'Cần Thơ' },
        { code: '53', name: 'Hậu Giang' },
        { code: '54', name: 'Sóc Trăng' },
        { code: '55', name: 'Bạc Liêu' },
        { code: '56', name: 'Cà Mau' }
      ],
      malaysianStates: [
        { code: 'JHR', name: 'Johor' },
        { code: 'KDH', name: 'Kedah' },
        { code: 'KTN', name: 'Kelantan' },
        { code: 'MLK', name: 'Malacca' },
        { code: 'NSN', name: 'Negeri Sembilan' },
        { code: 'PHG', name: 'Pahang' },
        { code: 'PRK', name: 'Perak' },
        { code: 'PLS', name: 'Perlis' },
        { code: 'PNG', name: 'Penang' },
        { code: 'SBH', name: 'Sabah' },
        { code: 'SWK', name: 'Sarawak' },
        { code: 'SGR', name: 'Selangor' },
        { code: 'TRG', name: 'Terengganu' },
        { code: 'KUL', name: 'Kuala Lumpur' },
        { code: 'LBN', name: 'Labuan' },
        { code: 'PJY', name: 'Putrajaya' }
      ],
      singaporeRegions: [
        { code: '01', name: 'Central Region' },
        { code: '02', name: 'East Region' },
        { code: '03', name: 'North Region' },
        { code: '04', name: 'North-East Region' },
        { code: '05', name: 'West Region' }
      ],
      philippineProvinces: [
        { code: 'ABR', name: 'Abra' },
        { code: 'AGN', name: 'Agusan del Norte' },
        { code: 'AGS', name: 'Agusan del Sur' },
        { code: 'AKL', name: 'Aklan' },
        { code: 'ALB', name: 'Albay' },
        { code: 'ANT', name: 'Antique' },
        { code: 'APA', name: 'Apayao' },
        { code: 'AUR', name: 'Aurora' },
        { code: 'BAS', name: 'Basilan' },
        { code: 'BAN', name: 'Bataan' },
        { code: 'BTN', name: 'Batanes' },
        { code: 'BTG', name: 'Batangas' },
        { code: 'BEN', name: 'Benguet' },
        { code: 'BIL', name: 'Biliran' },
        { code: 'BOH', name: 'Bohol' },
        { code: 'BUK', name: 'Bukidnon' },
        { code: 'BUL', name: 'Bulacan' },
        { code: 'CAG', name: 'Cagayan' },
        { code: 'CAN', name: 'Camarines Norte' },
        { code: 'CAS', name: 'Camarines Sur' },
        { code: 'CAM', name: 'Camiguin' },
        { code: 'CAP', name: 'Capiz' },
        { code: 'CAT', name: 'Catanduanes' },
        { code: 'CAV', name: 'Cavite' },
        { code: 'CEB', name: 'Cebu' },
        { code: 'COM', name: 'Compostela Valley' },
        { code: 'NCO', name: 'Cotabato' },
        { code: 'DAV', name: 'Davao del Norte' },
        { code: 'DAS', name: 'Davao del Sur' },
        { code: 'DAO', name: 'Davao Occidental' },
        { code: 'DAOR', name: 'Davao Oriental' },
        { code: 'DIN', name: 'Dinagat Islands' },
        { code: 'EAS', name: 'Eastern Samar' },
        { code: 'GUI', name: 'Guimaras' },
        { code: 'IFU', name: 'Ifugao' },
        { code: 'ILN', name: 'Ilocos Norte' },
        { code: 'ILS', name: 'Ilocos Sur' },
        { code: 'ILI', name: 'Iloilo' },
        { code: 'ISA', name: 'Isabela' },
        { code: 'KAL', name: 'Kalinga' },
        { code: 'LUN', name: 'La Union' },
        { code: 'LAG', name: 'Laguna' },
        { code: 'LAN', name: 'Lanao del Norte' },
        { code: 'LAS', name: 'Lanao del Sur' },
        { code: 'LEY', name: 'Leyte' },
        { code: 'MAG', name: 'Maguindanao' },
        { code: 'MAD', name: 'Marinduque' },
        { code: 'MAS', name: 'Masbate' },
        { code: 'MSC', name: 'Misamis Occidental' },
        { code: 'MSR', name: 'Misamis Oriental' },
        { code: 'MOU', name: 'Mountain Province' },
        { code: 'NEC', name: 'Negros Occidental' },
        { code: 'NER', name: 'Negros Oriental' },
        { code: 'NSA', name: 'Northern Samar' },
        { code: 'NUE', name: 'Nueva Ecija' },
        { code: 'NUV', name: 'Nueva Vizcaya' },
        { code: 'MDC', name: 'Occidental Mindoro' },
        { code: 'MDR', name: 'Oriental Mindoro' },
        { code: 'PLW', name: 'Palawan' },
        { code: 'PAM', name: 'Pampanga' },
        { code: 'PAN', name: 'Pangasinan' },
        { code: 'QUE', name: 'Quezon' },
        { code: 'QUI', name: 'Quirino' },
        { code: 'RIZ', name: 'Rizal' },
        { code: 'ROM', name: 'Romblon' },
        { code: 'SAR', name: 'Sarangani' },
        { code: 'SIQ', name: 'Siquijor' },
        { code: 'SOR', name: 'Sorsogon' },
        { code: 'SCO', name: 'South Cotabato' },
        { code: 'SLE', name: 'Southern Leyte' },
        { code: 'SUK', name: 'Sultan Kudarat' },
        { code: 'SLU', name: 'Sulu' },
        { code: 'SUN', name: 'Surigao del Norte' },
        { code: 'SUR', name: 'Surigao del Sur' },
        { code: 'TAR', name: 'Tarlac' },
        { code: 'TAW', name: 'Tawi-Tawi' },
        { code: 'ZMB', name: 'Zambales' },
        { code: 'ZAN', name: 'Zamboanga del Norte' },
        { code: 'ZAS', name: 'Zamboanga del Sur' },
        { code: 'ZSI', name: 'Zamboanga Sibugay' },
        { code: 'NCR', name: 'Metro Manila' }
      ],
      indonesianProvinces: [
        { code: 'AC', name: 'Aceh' },
        { code: 'SU', name: 'North Sumatra' },
        { code: 'SB', name: 'West Sumatra' },
        { code: 'RI', name: 'Riau' },
        { code: 'KR', name: 'Riau Islands' },
        { code: 'JA', name: 'Jambi' },
        { code: 'SS', name: 'South Sumatra' },
        { code: 'BB', name: 'Bangka Belitung' },
        { code: 'BE', name: 'Bengkulu' },
        { code: 'LA', name: 'Lampung' },
        { code: 'JK', name: 'Jakarta' },
        { code: 'JB', name: 'West Java' },
        { code: 'JT', name: 'Central Java' },
        { code: 'JI', name: 'East Java' },
        { code: 'BT', name: 'Banten' },
        { code: 'BA', name: 'Bali' },
        { code: 'NB', name: 'West Nusa Tenggara' },
        { code: 'NT', name: 'East Nusa Tenggara' },
        { code: 'KB', name: 'West Kalimantan' },
        { code: 'KT', name: 'Central Kalimantan' },
        { code: 'KS', name: 'South Kalimantan' },
        { code: 'KI', name: 'East Kalimantan' },
        { code: 'KU', name: 'North Kalimantan' },
        { code: 'SA', name: 'North Sulawesi' },
        { code: 'ST', name: 'Central Sulawesi' },
        { code: 'SG', name: 'South Sulawesi' },
        { code: 'SR', name: 'Southeast Sulawesi' },
        { code: 'GO', name: 'Gorontalo' },
        { code: 'MA', name: 'West Sulawesi' },
        { code: 'MU', name: 'Maluku' },
        { code: 'MU', name: 'North Maluku' },
        { code: 'PA', name: 'West Papua' },
        { code: 'PB', name: 'Papua' },
        { code: 'PS', name: 'Southwest Papua' },
        { code: 'PC', name: 'Central Papua' },
        { code: 'PE', name: 'Highland Papua' },
        { code: 'PU', name: 'South Papua' }
      ],
      pakistaniProvinces: [
        { code: 'PB', name: 'Punjab' },
        { code: 'SN', name: 'Sindh' },
        { code: 'KP', name: 'Khyber Pakhtunkhwa' },
        { code: 'BA', name: 'Balochistan' },
        { code: 'GB', name: 'Gilgit-Baltistan' },
        { code: 'AJK', name: 'Azad Jammu and Kashmir' },
        { code: 'ICT', name: 'Islamabad Capital Territory' }
      ],
      bangladeshiDivisions: [
        { code: 'A', name: 'Dhaka' },
        { code: 'B', name: 'Chittagong' },
        { code: 'C', name: 'Rajshahi' },
        { code: 'D', name: 'Khulna' },
        { code: 'E', name: 'Barisal' },
        { code: 'F', name: 'Sylhet' },
        { code: 'G', name: 'Rangpur' },
        { code: 'H', name: 'Mymensingh' }
      ],
      sriLankanProvinces: [
        { code: '1', name: 'Western Province' },
        { code: '2', name: 'Central Province' },
        { code: '3', name: 'Southern Province' },
        { code: '4', name: 'Northern Province' },
        { code: '5', name: 'Eastern Province' },
        { code: '6', name: 'North Western Province' },
        { code: '7', name: 'North Central Province' },
        { code: '8', name: 'Uva Province' },
        { code: '9', name: 'Sabaragamuwa Province' }
      ],
      newZealandRegions: [
        { code: 'AUK', name: 'Auckland' },
        { code: 'BOP', name: 'Bay of Plenty' },
        { code: 'CAN', name: 'Canterbury' },
        { code: 'GIS', name: 'Gisborne' },
        { code: 'HKB', name: 'Hawke\'s Bay' },
        { code: 'MWT', name: 'Manawatu-Wanganui' },
        { code: 'MBH', name: 'Marlborough' },
        { code: 'NSN', name: 'Nelson' },
        { code: 'NTL', name: 'Northland' },
        { code: 'OTA', name: 'Otago' },
        { code: 'STL', name: 'Southland' },
        { code: 'TAS', name: 'Tasman' },
        { code: 'TKI', name: 'Taranaki' },
        { code: 'WKO', name: 'Waikato' },
        { code: 'WGN', name: 'Wellington' },
        { code: 'WTC', name: 'West Coast' }
      ],
      // Major cities for common states/countries
      majorCities: {
        'US': {
          'CA': [
            { code: 'LA', name: 'Los Angeles' },
            { code: 'SF', name: 'San Francisco' },
            { code: 'SD', name: 'San Diego' },
            { code: 'SJ', name: 'San Jose' },
            { code: 'SAC', name: 'Sacramento' },
            { code: 'FRES', name: 'Fresno' },
            { code: 'LONG', name: 'Long Beach' },
            { code: 'OAK', name: 'Oakland' },
            { code: 'BAK', name: 'Bakersfield' },
            { code: 'ANA', name: 'Anaheim' },
            { code: 'SANTA', name: 'Santa Ana' },
            { code: 'RIV', name: 'Riverside' },
            { code: 'STOCK', name: 'Stockton' },
            { code: 'IRV', name: 'Irvine' },
            { code: 'CHUL', name: 'Chula Vista' },
            { code: 'FREM', name: 'Fremont' },
            { code: 'SANB', name: 'San Bernardino' },
            { code: 'MOD', name: 'Modesto' },
            { code: 'FONT', name: 'Fontana' },
            { code: 'OXN', name: 'Oxnard' },
            { code: 'MOR', name: 'Moreno Valley' },
            { code: 'HUNT', name: 'Huntington Beach' },
            { code: 'SANTA_CL', name: 'Santa Clarita' },
            { code: 'GLEN', name: 'Glendale' },
            { code: 'SANTA_RO', name: 'Santa Rosa' },
            { code: 'OCE', name: 'Oceanside' },
            { code: 'RAN', name: 'Rancho Cucamonga' },
            { code: 'ONT', name: 'Ontario' },
            { code: 'LAN', name: 'Lancaster' },
            { code: 'ELK', name: 'Elk Grove' },
            { code: 'COR', name: 'Corona' },
            { code: 'PALM', name: 'Palmdale' },
            { code: 'SAL', name: 'Salinas' },
            { code: 'POM', name: 'Pomona' },
            { code: 'HAY', name: 'Hayward' },
            { code: 'ESCON', name: 'Escondido' },
            { code: 'TOR', name: 'Torrance' },
            { code: 'SUNNY', name: 'Sunnyvale' },
            { code: 'ORAN', name: 'Orange' },
            { code: 'FULL', name: 'Fullerton' },
            { code: 'PAS', name: 'Pasadena' },
            { code: 'THOU', name: 'Thousand Oaks' },
            { code: 'VIS', name: 'Visalia' },
            { code: 'SIMI', name: 'Simi Valley' },
            { code: 'CONC', name: 'Concord' },
            { code: 'ROSE', name: 'Roseville' },
            { code: 'VAL', name: 'Vallejo' },
            { code: 'VICT', name: 'Victorville' },
            { code: 'FAIR', name: 'Fairfield' },
            { code: 'INGL', name: 'Inglewood' },
            { code: 'VENT', name: 'Ventura' }
          ],
          'NY': [
            { code: 'NYC', name: 'New York City' },
            { code: 'BUF', name: 'Buffalo' },
            { code: 'ROC', name: 'Rochester' },
            { code: 'SYR', name: 'Syracuse' },
            { code: 'ALB', name: 'Albany' },
            { code: 'YON', name: 'Yonkers' },
            { code: 'UTI', name: 'Utica' },
            { code: 'NEW', name: 'New Rochelle' },
            { code: 'MOUNT', name: 'Mount Vernon' },
            { code: 'SCH', name: 'Schenectady' },
            { code: 'WHIT', name: 'White Plains' },
            { code: 'HAMP', name: 'Hempstead' },
            { code: 'TROY', name: 'Troy' },
            { code: 'NIAG', name: 'Niagara Falls' },
            { code: 'BING', name: 'Binghamton' },
            { code: 'FREE', name: 'Freeport' },
            { code: 'VAL', name: 'Valley Stream' },
            { code: 'LONG', name: 'Long Beach' },
            { code: 'ROME', name: 'Rome' },
            { code: 'ITH', name: 'Ithaca' },
            { code: 'JAM', name: 'Jamestown' },
            { code: 'POUG', name: 'Poughkeepsie' },
            { code: 'ELM', name: 'Elmira' },
            { code: 'WATER', name: 'Watertown' },
            { code: 'KING', name: 'Kingston' },
            { code: 'PLAT', name: 'Plattsburgh' },
            { code: 'OSW', name: 'Oswego' },
            { code: 'MID', name: 'Middletown' },
            { code: 'GLEN', name: 'Glens Falls' },
            { code: 'LOCK', name: 'Lockport' }
          ],
          'TX': [
            { code: 'HOU', name: 'Houston' },
            { code: 'DAL', name: 'Dallas' },
            { code: 'SA', name: 'San Antonio' },
            { code: 'AUS', name: 'Austin' },
            { code: 'FW', name: 'Fort Worth' },
            { code: 'ELP', name: 'El Paso' },
            { code: 'ARL', name: 'Arlington' },
            { code: 'COR', name: 'Corpus Christi' },
            { code: 'PLA', name: 'Plano' },
            { code: 'LUB', name: 'Lubbock' },
            { code: 'LAR', name: 'Laredo' },
            { code: 'GAR', name: 'Garland' },
            { code: 'IRV', name: 'Irving' },
            { code: 'AMA', name: 'Amarillo' },
            { code: 'GRAND', name: 'Grand Prairie' },
            { code: 'BROW', name: 'Brownsville' },
            { code: 'MCK', name: 'McKinney' },
            { code: 'FRIS', name: 'Frisco' },
            { code: 'PAS', name: 'Pasadena' },
            { code: 'KIL', name: 'Killeen' },
            { code: 'MES', name: 'Mesquite' },
            { code: 'MCALL', name: 'McAllen' },
            { code: 'CAR', name: 'Carrollton' },
            { code: 'MID', name: 'Midland' },
            { code: 'DENT', name: 'Denton' },
            { code: 'ABI', name: 'Abilene' },
            { code: 'BEAU', name: 'Beaumont' },
            { code: 'ROUN', name: 'Round Rock' },
            { code: 'ODES', name: 'Odessa' },
            { code: 'WAC', name: 'Waco' },
            { code: 'RICH', name: 'Richardson' },
            { code: 'LEW', name: 'Lewisville' },
            { code: 'TYL', name: 'Tyler' },
            { code: 'PEAR', name: 'Pearland' },
            { code: 'COLL', name: 'College Station' },
            { code: 'SUG', name: 'Sugar Land' },
            { code: 'TEMP', name: 'Temple' },
            { code: 'EDIN', name: 'Edinburg' },
            { code: 'BED', name: 'Bedford' },
            { code: 'THE', name: 'The Woodlands' }
          ],
          'FL': [
            { code: 'MIA', name: 'Miami' },
            { code: 'ORL', name: 'Orlando' },
            { code: 'TB', name: 'Tampa' },
            { code: 'JAX', name: 'Jacksonville' },
            { code: 'TLH', name: 'Tallahassee' },
            { code: 'STP', name: 'St. Petersburg' },
            { code: 'HIAL', name: 'Hialeah' },
            { code: 'PORT', name: 'Port St. Lucie' },
            { code: 'CAPE', name: 'Cape Coral' },
            { code: 'FTL', name: 'Fort Lauderdale' },
            { code: 'PEM', name: 'Pembroke Pines' },
            { code: 'HOLL', name: 'Hollywood' },
            { code: 'MIR', name: 'Miramar' },
            { code: 'GAIN', name: 'Gainesville' },
            { code: 'COR', name: 'Coral Springs' },
            { code: 'MEL', name: 'Melbourne' },
            { code: 'PALM', name: 'Palm Bay' },
            { code: 'WEST', name: 'West Palm Beach' },
            { code: 'POM', name: 'Pompano Beach' },
            { code: 'LAKE', name: 'Lakeland' },
            { code: 'DAV', name: 'Davie' },
            { code: 'MIA_GAR', name: 'Miami Gardens' },
            { code: 'SUNR', name: 'Sunrise' },
            { code: 'PLAN', name: 'Plantation' },
            { code: 'BOCA', name: 'Boca Raton' },
            { code: 'DEL', name: 'Deltona' },
            { code: 'LAR', name: 'Largo' },
            { code: 'DEER', name: 'Deerfield Beach' },
            { code: 'BOY', name: 'Boynton Beach' },
            { code: 'LAUD', name: 'Lauderhill' },
            { code: 'FORT', name: 'Fort Myers' },
            { code: 'KISS', name: 'Kissimmee' },
            { code: 'HOM', name: 'Homestead' },
            { code: 'TAM', name: 'Tamiami' },
            { code: 'DELR', name: 'Delray Beach' },
            { code: 'DAY', name: 'Daytona Beach' },
            { code: 'NORTH', name: 'North Miami' },
            { code: 'WINT', name: 'Winter Haven' },
            { code: 'PENS', name: 'Pensacola' },
            { code: 'SAR', name: 'Sarasota' }
          ],
          'UT': [
            { code: 'SLC', name: 'Salt Lake City' },
            { code: 'WVC', name: 'West Valley City' },
            { code: 'PROVO', name: 'Provo' },
            { code: 'WJ', name: 'West Jordan' },
            { code: 'OREM', name: 'Orem' },
            { code: 'SANDY', name: 'Sandy' },
            { code: 'OGDEN', name: 'Ogden' },
            { code: 'LAYTON', name: 'Layton' },
            { code: 'TAYLORSVILLE', name: 'Taylorsville' },
            { code: 'MURRAY', name: 'Murray' },
            { code: 'BOUNTIFUL', name: 'Bountiful' },
            { code: 'DRAPER', name: 'Draper' },
            { code: 'RIVERTON', name: 'Riverton' },
            { code: 'ROY', name: 'Roy' },
            { code: 'PLEASANT_GROVE', name: 'Pleasant Grove' },
            { code: 'LEHI', name: 'Lehi' },
            { code: 'MIDVALE', name: 'Midvale' },
            { code: 'CLEARFIELD', name: 'Clearfield' },
            { code: 'AMERICAN_FORK', name: 'American Fork' },
            { code: 'SPANISH_FORK', name: 'Spanish Fork' },
            { code: 'SPRINGVILLE', name: 'Springville' },
            { code: 'PAYSON', name: 'Payson' },
            { code: 'HERRIMAN', name: 'Herriman' },
            { code: 'EAGLE_MOUNTAIN', name: 'Eagle Mountain' },
            { code: 'SARATOGA_SPRINGS', name: 'Saratoga Springs' },
            { code: 'CEDAR_CITY', name: 'Cedar City' },
            { code: 'ST_GEORGE', name: 'St. George' },
            { code: 'LOGAN', name: 'Logan' },
            { code: 'TOOELE', name: 'Tooele' },
            { code: 'PARK_CITY', name: 'Park City' }
          ],
          'AL': [
            { code: 'BHAM', name: 'Birmingham' },
            { code: 'MOB', name: 'Mobile' },
            { code: 'MGM', name: 'Montgomery' },
            { code: 'HSV', name: 'Huntsville' },
            { code: 'TUS', name: 'Tuscaloosa' }
          ],
          'AK': [
            { code: 'ANC', name: 'Anchorage' },
            { code: 'FAI', name: 'Fairbanks' },
            { code: 'JUN', name: 'Juneau' },
            { code: 'SIT', name: 'Sitka' },
            { code: 'WAS', name: 'Wasilla' }
          ],
          'AZ': [
            { code: 'PHX', name: 'Phoenix' },
            { code: 'TUC', name: 'Tucson' },
            { code: 'MES', name: 'Mesa' },
            { code: 'CHD', name: 'Chandler' },
            { code: 'SCO', name: 'Scottsdale' },
            { code: 'GLD', name: 'Glendale' },
            { code: 'GIL', name: 'Gilbert' },
            { code: 'TMP', name: 'Tempe' },
            { code: 'PEO', name: 'Peoria' },
            { code: 'SUP', name: 'Surprise' },
            { code: 'YUM', name: 'Yuma' },
            { code: 'AVON', name: 'Avondale' },
            { code: 'GOOD', name: 'Goodyear' },
            { code: 'FLAG', name: 'Flagstaff' },
            { code: 'BUCK', name: 'Buckeye' },
            { code: 'CASA', name: 'Casa Grande' },
            { code: 'SED', name: 'Sedona' },
            { code: 'PRES', name: 'Prescott' },
            { code: 'LAKE', name: 'Lake Havasu City' },
            { code: 'BULL', name: 'Bullhead City' },
            { code: 'SIER', name: 'Sierra Vista' },
            { code: 'MAR', name: 'Maricopa' },
            { code: 'ORO', name: 'Oro Valley' },
            { code: 'KING', name: 'Kingman' },
            { code: 'NOG', name: 'Nogales' },
            { code: 'SAFF', name: 'Safford' },
            { code: 'SHOW', name: 'Show Low' },
            { code: 'COOL', name: 'Coolidge' },
            { code: 'SOM', name: 'Somerton' },
            { code: 'DOUG', name: 'Douglas' }
          ],
          'AR': [
            { code: 'LR', name: 'Little Rock' },
            { code: 'FT', name: 'Fort Smith' },
            { code: 'FAY', name: 'Fayetteville' },
            { code: 'JON', name: 'Jonesboro' },
            { code: 'NOR', name: 'North Little Rock' }
          ],
          'CO': [
            { code: 'DEN', name: 'Denver' },
            { code: 'COLS', name: 'Colorado Springs' },
            { code: 'AUR', name: 'Aurora' },
            { code: 'FTCO', name: 'Fort Collins' },
            { code: 'LC', name: 'Lakewood' },
            { code: 'TH', name: 'Thornton' },
            { code: 'ARV', name: 'Arvada' },
            { code: 'WE', name: 'Westminster' },
            { code: 'PUE', name: 'Pueblo' },
            { code: 'GJR', name: 'Greeley' }
          ],
          'CT': [
            { code: 'BRD', name: 'Bridgeport' },
            { code: 'NH', name: 'New Haven' },
            { code: 'HART', name: 'Hartford' },
            { code: 'STM', name: 'Stamford' },
            { code: 'WAT', name: 'Waterbury' },
            { code: 'NOR', name: 'Norwalk' },
            { code: 'DAN', name: 'Danbury' },
            { code: 'NB', name: 'New Britain' },
            { code: 'WEST', name: 'West Hartford' },
            { code: 'GREEN', name: 'Greenwich' }
          ],
          'DE': [
            { code: 'WIL', name: 'Wilmington' },
            { code: 'DOV', name: 'Dover' },
            { code: 'NEW', name: 'Newark' },
            { code: 'MID', name: 'Middletown' },
            { code: 'SMI', name: 'Smyrna' }
          ],
          'GA': [
            { code: 'ATL', name: 'Atlanta' },
            { code: 'COL', name: 'Columbus' },
            { code: 'AUG', name: 'Augusta' },
            { code: 'SAV', name: 'Savannah' },
            { code: 'ATH', name: 'Athens' },
            { code: 'SAND', name: 'Sandy Springs' },
            { code: 'ROS', name: 'Roswell' },
            { code: 'MAC', name: 'Macon' },
            { code: 'JOH', name: 'Johns Creek' },
            { code: 'ALP', name: 'Alpharetta' },
            { code: 'WARN', name: 'Warner Robins' },
            { code: 'ALB', name: 'Albany' },
            { code: 'VAL', name: 'Valdosta' },
            { code: 'SMY', name: 'Smyrna' },
            { code: 'DUN', name: 'Dunwoody' },
            { code: 'ROME', name: 'Rome' },
            { code: 'EAST', name: 'East Point' },
            { code: 'PEA', name: 'Peachtree Corners' },
            { code: 'GAIN', name: 'Gainesville' },
            { code: 'HIN', name: 'Hinesville' },
            { code: 'DAL', name: 'Dalton' },
            { code: 'LAWR', name: 'Lawrenceville' },
            { code: 'STON', name: 'Stonecrest' },
            { code: 'DOU', name: 'Douglasville' },
            { code: 'KEN', name: 'Kennesaw' },
            { code: 'NEW', name: 'Newnan' },
            { code: 'CAR', name: 'Carrollton' },
            { code: 'BRUN', name: 'Brunswick' },
            { code: 'THOM', name: 'Thomasville' },
            { code: 'MARI', name: 'Marietta' }
          ],
          'HI': [
            { code: 'HON', name: 'Honolulu' },
            { code: 'EAST', name: 'East Honolulu' },
            { code: 'PEA', name: 'Pearl City' },
            { code: 'HILO', name: 'Hilo' },
            { code: 'KAI', name: 'Kailua' }
          ],
          'ID': [
            { code: 'BOI', name: 'Boise' },
            { code: 'NAM', name: 'Nampa' },
            { code: 'MER', name: 'Meridian' },
            { code: 'IDA', name: 'Idaho Falls' },
            { code: 'POC', name: 'Pocatello' }
          ],
          'IL': [
            { code: 'CHI', name: 'Chicago' },
            { code: 'AUR', name: 'Aurora' },
            { code: 'ROC', name: 'Rockford' },
            { code: 'JOL', name: 'Joliet' },
            { code: 'NAV', name: 'Naperville' },
            { code: 'SPR', name: 'Springfield' },
            { code: 'PEO', name: 'Peoria' },
            { code: 'ELG', name: 'Elgin' },
            { code: 'WEGO', name: 'Waukegan' },
            { code: 'CIC', name: 'Cicero' },
            { code: 'CHA', name: 'Champaign' },
            { code: 'BLOOM', name: 'Bloomington' },
            { code: 'ARL', name: 'Arlington Heights' },
            { code: 'EVAN', name: 'Evanston' },
            { code: 'DEC', name: 'Decatur' },
            { code: 'SCHA', name: 'Schaumburg' },
            { code: 'BOL', name: 'Bolingbrook' },
            { code: 'PAL', name: 'Palatine' },
            { code: 'SKO', name: 'Skokie' },
            { code: 'DES', name: 'Des Plaines' },
            { code: 'ORL', name: 'Orland Park' },
            { code: 'TIN', name: 'Tinley Park' },
            { code: 'OAK', name: 'Oak Lawn' },
            { code: 'BER', name: 'Berwyn' },
            { code: 'MOUNT', name: 'Mount Prospect' },
            { code: 'NOR', name: 'Normal' },
            { code: 'WHE', name: 'Wheaton' },
            { code: 'HOFF', name: 'Hoffman Estates' },
            { code: 'OAKP', name: 'Oak Park' },
            { code: 'DOWN', name: 'Downers Grove' }
          ],
          'IN': [
            { code: 'IND', name: 'Indianapolis' },
            { code: 'FTW', name: 'Fort Wayne' },
            { code: 'EVA', name: 'Evansville' },
            { code: 'SOU', name: 'South Bend' },
            { code: 'CARM', name: 'Carmel' },
            { code: 'FISH', name: 'Fishers' },
            { code: 'BLOOM', name: 'Bloomington' },
            { code: 'HAM', name: 'Hammond' },
            { code: 'GARY', name: 'Gary' },
            { code: 'MUN', name: 'Muncie' }
          ],
          'IA': [
            { code: 'DSM', name: 'Des Moines' },
            { code: 'CED', name: 'Cedar Rapids' },
            { code: 'DAV', name: 'Davenport' },
            { code: 'SIU', name: 'Sioux City' },
            { code: 'IOWA', name: 'Iowa City' }
          ],
          'KS': [
            { code: 'WIC', name: 'Wichita' },
            { code: 'OVE', name: 'Overland Park' },
            { code: 'KC', name: 'Kansas City' },
            { code: 'TOP', name: 'Topeka' },
            { code: 'OLA', name: 'Olathe' }
          ],
          'KY': [
            { code: 'LEX', name: 'Lexington' },
            { code: 'LOU', name: 'Louisville' },
            { code: 'BOW', name: 'Bowling Green' },
            { code: 'OWN', name: 'Owensboro' },
            { code: 'COV', name: 'Covington' }
          ],
          'LA': [
            { code: 'NOL', name: 'New Orleans' },
            { code: 'BR', name: 'Baton Rouge' },
            { code: 'SHRE', name: 'Shreveport' },
            { code: 'LAF', name: 'Lafayette' },
            { code: 'LB', name: 'Lake Charles' }
          ],
          'ME': [
            { code: 'POR', name: 'Portland' },
            { code: 'LEW', name: 'Lewiston' },
            { code: 'BAN', name: 'Bangor' },
            { code: 'SAC', name: 'Saco' },
            { code: 'AUB', name: 'Auburn' }
          ],
          'MD': [
            { code: 'BAL', name: 'Baltimore' },
            { code: 'FRED', name: 'Frederick' },
            { code: 'GAITH', name: 'Gaithersburg' },
            { code: 'ROC', name: 'Rockville' },
            { code: 'BOW', name: 'Bowie' }
          ],
          'MA': [
            { code: 'BOS', name: 'Boston' },
            { code: 'WOR', name: 'Worcester' },
            { code: 'SPR', name: 'Springfield' },
            { code: 'LOW', name: 'Lowell' },
            { code: 'CAM', name: 'Cambridge' },
            { code: 'NEW', name: 'New Bedford' },
            { code: 'BRO', name: 'Brockton' },
            { code: 'QUI', name: 'Quincy' },
            { code: 'LYN', name: 'Lynn' },
            { code: 'FAL', name: 'Fall River' },
            { code: 'NEWT', name: 'Newton' },
            { code: 'LAWR', name: 'Lawrence' },
            { code: 'SOM', name: 'Somerville' },
            { code: 'FRAM', name: 'Framingham' },
            { code: 'HAVER', name: 'Haverhill' },
            { code: 'WAL', name: 'Waltham' },
            { code: 'MAL', name: 'Malden' },
            { code: 'BROO', name: 'Brookline' },
            { code: 'QUIN', name: 'Quincy' },
            { code: 'PIT', name: 'Pittsfield' },
            { code: 'BAR', name: 'Barnstable' },
            { code: 'LEO', name: 'Leominster' },
            { code: 'CHIC', name: 'Chicopee' },
            { code: 'TAUN', name: 'Taunton' },
            { code: 'REV', name: 'Revere' },
            { code: 'PEA', name: 'Peabody' },
            { code: 'METH', name: 'Methuen' },
            { code: 'EVER', name: 'Everett' },
            { code: 'SAL', name: 'Salem' }
          ],
          'MI': [
            { code: 'DET', name: 'Detroit' },
            { code: 'GR', name: 'Grand Rapids' },
            { code: 'WAR', name: 'Warren' },
            { code: 'STER', name: 'Sterling Heights' },
            { code: 'ANN', name: 'Ann Arbor' },
            { code: 'LANS', name: 'Lansing' },
            { code: 'TROY', name: 'Troy' },
            { code: 'FLI', name: 'Flint' },
            { code: 'DEA', name: 'Dearborn' },
            { code: 'LIV', name: 'Livonia' },
            { code: 'FARM', name: 'Farmington Hills' },
            { code: 'KAL', name: 'Kalamazoo' },
            { code: 'WEST', name: 'Westland' },
            { code: 'WYOM', name: 'Wyoming' },
            { code: 'SOUTH', name: 'Southfield' },
            { code: 'ROCH', name: 'Rochester Hills' },
            { code: 'TAYL', name: 'Taylor' },
            { code: 'PON', name: 'Pontiac' },
            { code: 'ST_CL', name: 'St. Clair Shores' },
            { code: 'ROY', name: 'Royal Oak' },
            { code: 'NOV', name: 'Novi' },
            { code: 'BATT', name: 'Battle Creek' },
            { code: 'SAG', name: 'Saginaw' },
            { code: 'MUSK', name: 'Muskegon' },
            { code: 'HOLL', name: 'Holland' },
            { code: 'BAY', name: 'Bay City' },
            { code: 'JACK', name: 'Jackson' },
            { code: 'PORT', name: 'Portage' },
            { code: 'MID', name: 'Midland' },
            { code: 'MAR', name: 'Marquette' }
          ],
          'MN': [
            { code: 'MIN', name: 'Minneapolis' },
            { code: 'SP', name: 'Saint Paul' },
            { code: 'ROCH', name: 'Rochester' },
            { code: 'DUL', name: 'Duluth' },
            { code: 'BLMN', name: 'Bloomington' }
          ],
          'MS': [
            { code: 'JAC', name: 'Jackson' },
            { code: 'GULF', name: 'Gulfport' },
            { code: 'SOU', name: 'Southaven' },
            { code: 'HAN', name: 'Hattiesburg' },
            { code: 'BIL', name: 'Biloxi' }
          ],
          'MO': [
            { code: 'KC', name: 'Kansas City' },
            { code: 'SL', name: 'St. Louis' },
            { code: 'SPR', name: 'Springfield' },
            { code: 'COL', name: 'Columbia' },
            { code: 'IND', name: 'Independence' }
          ],
          'MT': [
            { code: 'BIL', name: 'Billings' },
            { code: 'MISS', name: 'Missoula' },
            { code: 'GRE', name: 'Great Falls' },
            { code: 'BZ', name: 'Bozeman' },
            { code: 'BUTT', name: 'Butte' }
          ],
          'NE': [
            { code: 'OM', name: 'Omaha' },
            { code: 'LIN', name: 'Lincoln' },
            { code: 'BEL', name: 'Bellevue' },
            { code: 'GRAN', name: 'Grand Island' },
            { code: 'KEAR', name: 'Kearney' }
          ],
          'NV': [
            { code: 'LV', name: 'Las Vegas' },
            { code: 'HEND', name: 'Henderson' },
            { code: 'REN', name: 'Reno' },
            { code: 'NORTH', name: 'North Las Vegas' },
            { code: 'SUN', name: 'Sparks' }
          ],
          'NH': [
            { code: 'MAN', name: 'Manchester' },
            { code: 'NASH', name: 'Nashua' },
            { code: 'CON', name: 'Concord' },
            { code: 'DER', name: 'Derry' },
            { code: 'ROCH', name: 'Rochester' }
          ],
          'NJ': [
            { code: 'NEW', name: 'Newark' },
            { code: 'JC', name: 'Jersey City' },
            { code: 'PAT', name: 'Paterson' },
            { code: 'ELIZ', name: 'Elizabeth' },
            { code: 'EDIS', name: 'Edison' },
            { code: 'WOOD', name: 'Woodbridge' },
            { code: 'LAKE', name: 'Lakewood' },
            { code: 'HAM', name: 'Hamilton' },
            { code: 'HACK', name: 'Hackensack' },
            { code: 'CAM', name: 'Camden' }
          ],
          'NM': [
            { code: 'ALB', name: 'Albuquerque' },
            { code: 'LAS', name: 'Las Cruces' },
            { code: 'RIO', name: 'Rio Rancho' },
            { code: 'SANTA', name: 'Santa Fe' },
            { code: 'ROS', name: 'Roswell' }
          ],
          'NC': [
            { code: 'CHAR', name: 'Charlotte' },
            { code: 'RAL', name: 'Raleigh' },
            { code: 'GRE', name: 'Greensboro' },
            { code: 'DUR', name: 'Durham' },
            { code: 'WIN', name: 'Winston-Salem' },
            { code: 'FI', name: 'Fayetteville' },
            { code: 'CARY', name: 'Cary' },
            { code: 'WIL', name: 'Wilmington' },
            { code: 'HIGH', name: 'High Point' },
            { code: 'CON', name: 'Concord' },
            { code: 'ASHE', name: 'Asheville' },
            { code: 'GAST', name: 'Gastonia' },
            { code: 'JACK', name: 'Jacksonville' },
            { code: 'CHAP', name: 'Chapel Hill' },
            { code: 'ROCK', name: 'Rocky Mount' },
            { code: 'BURL', name: 'Burlington' },
            { code: 'WILM', name: 'Wilson' },
            { code: 'HUNT', name: 'Huntersville' },
            { code: 'KAN', name: 'Kannapolis' },
            { code: 'AYD', name: 'Apex' },
            { code: 'HICK', name: 'Hickory' },
            { code: 'GOLD', name: 'Goldsboro' },
            { code: 'MON', name: 'Monroe' },
            { code: 'SAL', name: 'Salisbury' },
            { code: 'THOM', name: 'Thomasville' },
            { code: 'NEW', name: 'New Bern' },
            { code: 'STAT', name: 'Statesville' },
            { code: 'SAN', name: 'Sanford' },
            { code: 'MAT', name: 'Matthews' },
            { code: 'MINT', name: 'Mint Hill' }
          ],
          'ND': [
            { code: 'FAR', name: 'Fargo' },
            { code: 'BIS', name: 'Bismarck' },
            { code: 'GF', name: 'Grand Forks' },
            { code: 'MIN', name: 'Minot' },
            { code: 'WEST', name: 'West Fargo' }
          ],
          'OH': [
            { code: 'COL', name: 'Columbus' },
            { code: 'CLE', name: 'Cleveland' },
            { code: 'CIN', name: 'Cincinnati' },
            { code: 'TOL', name: 'Toledo' },
            { code: 'AKR', name: 'Akron' },
            { code: 'DAY', name: 'Dayton' },
            { code: 'PAR', name: 'Parma' },
            { code: 'CANT', name: 'Canton' },
            { code: 'YOUNG', name: 'Youngstown' },
            { code: 'LOR', name: 'Lorain' },
            { code: 'HAM', name: 'Hamilton' },
            { code: 'SPR', name: 'Springfield' },
            { code: 'KET', name: 'Kettering' },
            { code: 'ELY', name: 'Elyria' },
            { code: 'LAKE', name: 'Lakewood' },
            { code: 'CUU', name: 'Cuyahoga Falls' },
            { code: 'MID', name: 'Middletown' },
            { code: 'NEW', name: 'Newark' },
            { code: 'MAN', name: 'Mansfield' },
            { code: 'MEN', name: 'Mentor' },
            { code: 'BEAV', name: 'Beavercreek' },
            { code: 'CLE_H', name: 'Cleveland Heights' },
            { code: 'STR', name: 'Strongsville' },
            { code: 'FAIR', name: 'Fairborn' },
            { code: 'FIND', name: 'Findlay' },
            { code: 'WARR', name: 'Warren' },
            { code: 'LAN', name: 'Lancaster' },
            { code: 'LIMA', name: 'Lima' },
            { code: 'HUB', name: 'Huber Heights' },
            { code: 'WEST', name: 'Westerville' }
          ],
          'OK': [
            { code: 'OKC', name: 'Oklahoma City' },
            { code: 'TUL', name: 'Tulsa' },
            { code: 'NOR', name: 'Norman' },
            { code: 'BRO', name: 'Broken Arrow' },
            { code: 'LAW', name: 'Lawton' }
          ],
          'OR': [
            { code: 'POR', name: 'Portland' },
            { code: 'SAL', name: 'Salem' },
            { code: 'EUG', name: 'Eugene' },
            { code: 'GRA', name: 'Gresham' },
            { code: 'HIL', name: 'Hillsboro' }
          ],
          'PA': [
            { code: 'PHI', name: 'Philadelphia' },
            { code: 'PIT', name: 'Pittsburgh' },
            { code: 'ALL', name: 'Allentown' },
            { code: 'ER', name: 'Erie' },
            { code: 'READ', name: 'Reading' },
            { code: 'SCR', name: 'Scranton' },
            { code: 'BETH', name: 'Bethlehem' },
            { code: 'LAN', name: 'Lancaster' },
            { code: 'HARR', name: 'Harrisburg' },
            { code: 'ALT', name: 'Altoona' },
            { code: 'YOR', name: 'York' },
            { code: 'STAT', name: 'State College' },
            { code: 'WIL', name: 'Wilkes-Barre' },
            { code: 'CHE', name: 'Chester' },
            { code: 'WILL', name: 'Williamsport' },
            { code: 'NOR', name: 'Norristown' },
            { code: 'JOHN', name: 'Johnstown' },
            { code: 'WASH', name: 'Washington' },
            { code: 'NEW', name: 'New Castle' },
            { code: 'MON', name: 'Monroeville' },
            { code: 'LEB', name: 'Lebanon' },
            { code: 'CHAM', name: 'Chambersburg' },
            { code: 'MCK', name: 'McKeesport' },
            { code: 'ERIE', name: 'Erie' },
            { code: 'NEWC', name: 'New Kensington' },
            { code: 'WEST', name: 'West Chester' },
            { code: 'KING', name: 'Kingston' },
            { code: 'PLY', name: 'Plymouth' },
            { code: 'BUT', name: 'Butler' },
            { code: 'GRE', name: 'Greensburg' }
          ],
          'RI': [
            { code: 'PROV', name: 'Providence' },
            { code: 'WAR', name: 'Warwick' },
            { code: 'CRAN', name: 'Cranston' },
            { code: 'PAW', name: 'Pawtucket' },
            { code: 'EAST', name: 'East Providence' }
          ],
          'SC': [
            { code: 'CHAR', name: 'Charleston' },
            { code: 'COL', name: 'Columbia' },
            { code: 'NCH', name: 'North Charleston' },
            { code: 'GREE', name: 'Greenville' },
            { code: 'ROCK', name: 'Rock Hill' },
            { code: 'MP', name: 'Mount Pleasant' },
            { code: 'SUM', name: 'Summerville' },
            { code: 'HHI', name: 'Hilton Head Island' },
            { code: 'FLO', name: 'Florence' },
            { code: 'FM', name: 'Fort Mill' },
            { code: 'SPA', name: 'Spartanburg' },
            { code: 'AIK', name: 'Aiken' },
            { code: 'MB', name: 'Myrtle Beach' },
            { code: 'SUT', name: 'Sumter' },
            { code: 'AND', name: 'Anderson' },
            { code: 'GRE', name: 'Greer' },
            { code: 'MAU', name: 'Mauldin' },
            { code: 'GWD', name: 'Greenwood' },
            { code: 'TAY', name: 'Taylors' },
            { code: 'GC', name: 'Goose Creek' },
            { code: 'BLU', name: 'Bluffton' },
            { code: 'LEX', name: 'Lexington' },
            { code: 'CAY', name: 'Cayce' },
            { code: 'ORAN', name: 'Orangeburg' },
            { code: 'CON', name: 'Conway' },
            { code: 'HAN', name: 'Hanahan' },
            { code: 'SIM', name: 'Simpsonville' },
            { code: 'EAS', name: 'Easley' },
            { code: 'GAF', name: 'Gaffney' },
            { code: 'BEAU', name: 'Beaufort' },
            { code: 'CAM', name: 'Camden' }
          ],
          'SD': [
            { code: 'SIO', name: 'Sioux Falls' },
            { code: 'RAP', name: 'Rapid City' },
            { code: 'ABER', name: 'Aberdeen' },
            { code: 'BROOK', name: 'Brookings' },
            { code: 'WAT', name: 'Watertown' }
          ],
          'TN': [
            { code: 'MEM', name: 'Memphis' },
            { code: 'NASH', name: 'Nashville' },
            { code: 'KNOX', name: 'Knoxville' },
            { code: 'CHAT', name: 'Chattanooga' },
            { code: 'CLAK', name: 'Clarksville' }
          ],
          'VT': [
            { code: 'BURL', name: 'Burlington' },
            { code: 'ESJ', name: 'Essex' },
            { code: 'SOU', name: 'South Burlington' },
            { code: 'COL', name: 'Colchester' },
            { code: 'MON', name: 'Montpelier' }
          ],
          'VA': [
            { code: 'VIR', name: 'Virginia Beach' },
            { code: 'NOR', name: 'Norfolk' },
            { code: 'CHES', name: 'Chesapeake' },
            { code: 'RICH', name: 'Richmond' },
            { code: 'NEWP', name: 'Newport News' },
            { code: 'ALEX', name: 'Alexandria' },
            { code: 'HAM', name: 'Hampton' },
            { code: 'POR', name: 'Portsmouth' },
            { code: 'ROA', name: 'Roanoke' },
            { code: 'SUFF', name: 'Suffolk' }
          ],
          'WA': [
            { code: 'SEA', name: 'Seattle' },
            { code: 'SPO', name: 'Spokane' },
            { code: 'TAC', name: 'Tacoma' },
            { code: 'VAN', name: 'Vancouver' },
            { code: 'BEL', name: 'Bellevue' },
            { code: 'EVE', name: 'Everett' },
            { code: 'KENT', name: 'Kent' },
            { code: 'RENT', name: 'Renton' },
            { code: 'SPO', name: 'Spokane Valley' },
            { code: 'FED', name: 'Federal Way' },
            { code: 'YAK', name: 'Yakima' },
            { code: 'BELL', name: 'Bellingham' },
            { code: 'KEN', name: 'Kennewick' },
            { code: 'AUB', name: 'Auburn' },
            { code: 'PAS', name: 'Pasco' },
            { code: 'MARY', name: 'Marysville' },
            { code: 'LAKE', name: 'Lakewood' },
            { code: 'RED', name: 'Redmond' },
            { code: 'SHOR', name: 'Shoreline' },
            { code: 'RICH', name: 'Richland' },
            { code: 'KIRK', name: 'Kirkland' },
            { code: 'BUR', name: 'Burien' },
            { code: 'OLY', name: 'Olympia' },
            { code: 'LYN', name: 'Lynnwood' },
            { code: 'BREM', name: 'Bremerton' },
            { code: 'LACE', name: 'Lacey' },
            { code: 'LONG', name: 'Longview' },
            { code: 'PULL', name: 'Pullman' },
            { code: 'WEN', name: 'Wenatchee' }
          ],
          'WV': [
            { code: 'CHAR', name: 'Charleston' },
            { code: 'HUNT', name: 'Huntington' },
            { code: 'PAR', name: 'Parkersburg' },
            { code: 'MOR', name: 'Morgantown' },
            { code: 'WHEE', name: 'Wheeling' }
          ],
          'WI': [
            { code: 'MIL', name: 'Milwaukee' },
            { code: 'MAD', name: 'Madison' },
            { code: 'GRE', name: 'Green Bay' },
            { code: 'KEN', name: 'Kenosha' },
            { code: 'RAC', name: 'Racine' }
          ],
          'WY': [
            { code: 'CHE', name: 'Cheyenne' },
            { code: 'CAS', name: 'Casper' },
            { code: 'LAR', name: 'Laramie' },
            { code: 'GIL', name: 'Gillette' },
            { code: 'ROCK', name: 'Rock Springs' }
          ],
          'DC': [
            { code: 'WASH', name: 'Washington' }
          ]
        },
        'CA': {
          'ON': [
            { code: 'TOR', name: 'Toronto' },
            { code: 'OTT', name: 'Ottawa' },
            { code: 'HAM', name: 'Hamilton' },
            { code: 'LON', name: 'London' },
            { code: 'KIT', name: 'Kitchener' }
          ],
          'QC': [
            { code: 'MON', name: 'Montreal' },
            { code: 'QUE', name: 'Quebec City' },
            { code: 'LAV', name: 'Laval' },
            { code: 'GAT', name: 'Gatineau' },
            { code: 'LON', name: 'Longueuil' }
          ],
          'BC': [
            { code: 'VAN', name: 'Vancouver' },
            { code: 'VIC', name: 'Victoria' },
            { code: 'SUR', name: 'Surrey' },
            { code: 'BUR', name: 'Burnaby' },
            { code: 'RICH', name: 'Richmond' }
          ],
          'AB': [
            { code: 'CAL', name: 'Calgary' },
            { code: 'EDM', name: 'Edmonton' },
            { code: 'RED', name: 'Red Deer' },
            { code: 'LETH', name: 'Lethbridge' },
            { code: 'STAL', name: 'St. Albert' }
          ]
        },
        'GB': {
          'ENG': [
            { code: 'LON', name: 'London' },
            { code: 'BIR', name: 'Birmingham' },
            { code: 'MAN', name: 'Manchester' },
            { code: 'LEE', name: 'Leeds' },
            { code: 'LIV', name: 'Liverpool' }
          ],
          'SCT': [
            { code: 'EDI', name: 'Edinburgh' },
            { code: 'GLA', name: 'Glasgow' },
            { code: 'ABE', name: 'Aberdeen' },
            { code: 'DUND', name: 'Dundee' },
            { code: 'INV', name: 'Inverness' }
          ],
          'WLS': [
            { code: 'CAR', name: 'Cardiff' },
            { code: 'SWA', name: 'Swansea' },
            { code: 'NEW', name: 'Newport' },
            { code: 'WRE', name: 'Wrexham' },
            { code: 'BAR', name: 'Barry' }
          ],
          'NIR': [
            { code: 'BEL', name: 'Belfast' },
            { code: 'DER', name: 'Derry' },
            { code: 'LIS', name: 'Lisburn' },
            { code: 'NEW', name: 'Newry' },
            { code: 'BAN', name: 'Bangor' }
          ]
        },
        'AU': {
          'NSW': [
            { code: 'SYD', name: 'Sydney' },
            { code: 'NEW', name: 'Newcastle' },
            { code: 'WOL', name: 'Wollongong' },
            { code: 'WAG', name: 'Wagga Wagga' },
            { code: 'ALB', name: 'Albury' }
          ],
          'VIC': [
            { code: 'MEL', name: 'Melbourne' },
            { code: 'GEE', name: 'Geelong' },
            { code: 'BAL', name: 'Ballarat' },
            { code: 'BEN', name: 'Bendigo' },
            { code: 'SHE', name: 'Shepparton' }
          ],
          'QLD': [
            { code: 'BRI', name: 'Brisbane' },
            { code: 'GOL', name: 'Gold Coast' },
            { code: 'TOW', name: 'Townsville' },
            { code: 'CAI', name: 'Cairns' },
            { code: 'TOO', name: 'Toowoomba' }
          ],
          'WA': [
            { code: 'PER', name: 'Perth' },
            { code: 'FRE', name: 'Fremantle' },
            { code: 'BUN', name: 'Bunbury' },
            { code: 'GER', name: 'Geraldton' },
            { code: 'KAL', name: 'Kalgoorlie' }
          ],
          'SA': [
            { code: 'ADE', name: 'Adelaide' },
            { code: 'MOUNT', name: 'Mount Gambier' },
            { code: 'WHY', name: 'Whyalla' },
            { code: 'MUR', name: 'Murray Bridge' },
            { code: 'POR', name: 'Port Augusta' }
          ]
        },
        'DE': {
          'BY': [
            { code: 'MUN', name: 'Munich' },
            { code: 'NUR', name: 'Nuremberg' },
            { code: 'AUG', name: 'Augsburg' },
            { code: 'REG', name: 'Regensburg' },
            { code: 'WUR', name: 'Würzburg' }
          ],
          'NW': [
            { code: 'COL', name: 'Cologne' },
            { code: 'DUS', name: 'Düsseldorf' },
            { code: 'DOR', name: 'Dortmund' },
            { code: 'ESS', name: 'Essen' },
            { code: 'DUI', name: 'Duisburg' }
          ],
          'BE': [
            { code: 'BER', name: 'Berlin' }
          ],
          'HH': [
            { code: 'HAM', name: 'Hamburg' }
          ],
          'HE': [
            { code: 'FRA', name: 'Frankfurt' },
            { code: 'WIE', name: 'Wiesbaden' },
            { code: 'KAS', name: 'Kassel' },
            { code: 'DAR', name: 'Darmstadt' },
            { code: 'OFF', name: 'Offenbach' }
          ]
        },
        'FR': {
          'IDF': [
            { code: 'PAR', name: 'Paris' },
            { code: 'BOU', name: 'Boulogne-Billancourt' },
            { code: 'SAI', name: 'Saint-Denis' },
            { code: 'ARG', name: 'Argenteuil' },
            { code: 'MON', name: 'Montreuil' }
          ],
          'ARA': [
            { code: 'LYO', name: 'Lyon' },
            { code: 'SAI', name: 'Saint-Étienne' },
            { code: 'GREN', name: 'Grenoble' },
            { code: 'VIL', name: 'Villeurbanne' },
            { code: 'CLER', name: 'Clermont-Ferrand' }
          ],
          'PAC': [
            { code: 'MAR', name: 'Marseille' },
            { code: 'NIC', name: 'Nice' },
            { code: 'TOU', name: 'Toulon' },
            { code: 'AIX', name: 'Aix-en-Provence' },
            { code: 'AVI', name: 'Avignon' }
          ]
        },
        'IN': {
          'MH': [
            { code: 'MUM', name: 'Mumbai' },
            { code: 'PUN', name: 'Pune' },
            { code: 'NAG', name: 'Nagpur' },
            { code: 'NAS', name: 'Nashik' },
            { code: 'AUR', name: 'Aurangabad' }
          ],
          'DL': [
            { code: 'DEL', name: 'Delhi' },
            { code: 'NEW', name: 'New Delhi' }
          ],
          'KA': [
            { code: 'BAN', name: 'Bangalore' },
            { code: 'MYS', name: 'Mysore' },
            { code: 'HUB', name: 'Hubli' },
            { code: 'MAN', name: 'Mangalore' },
            { code: 'BEL', name: 'Belgaum' }
          ],
          'TN': [
            { code: 'CHE', name: 'Chennai' },
            { code: 'COI', name: 'Coimbatore' },
            { code: 'MAD', name: 'Madurai' },
            { code: 'TIR', name: 'Tiruchirappalli' },
            { code: 'SAL', name: 'Salem' }
          ],
          'GJ': [
            { code: 'AHM', name: 'Ahmedabad' },
            { code: 'SUR', name: 'Surat' },
            { code: 'VAD', name: 'Vadodara' },
            { code: 'RAJ', name: 'Rajkot' },
            { code: 'BHA', name: 'Bhavnagar' }
          ]
        },
        'JP': {
          'TOK': [
            { code: 'TOK', name: 'Tokyo' },
            { code: 'YOK', name: 'Yokohama' },
            { code: 'KAW', name: 'Kawasaki' },
            { code: 'SAI', name: 'Saitama' },
            { code: 'CHI', name: 'Chiba' }
          ],
          'OSA': [
            { code: 'OSA', name: 'Osaka' },
            { code: 'SAK', name: 'Sakai' },
            { code: 'HIG', name: 'Higashiosaka' },
            { code: 'YAO', name: 'Yao' },
            { code: 'SUI', name: 'Suita' }
          ],
          'KYO': [
            { code: 'KYO', name: 'Kyoto' },
            { code: 'UJI', name: 'Uji' },
            { code: 'KUS', name: 'Kusatsu' },
            { code: 'MAI', name: 'Maizuru' },
            { code: 'AYA', name: 'Ayabe' }
          ]
        },
        'CN': {
          'BJ': [
            { code: 'BEI', name: 'Beijing' }
          ],
          'SH': [
            { code: 'SHA', name: 'Shanghai' }
          ],
          'GD': [
            { code: 'GUA', name: 'Guangzhou' },
            { code: 'SHE', name: 'Shenzhen' },
            { code: 'DON', name: 'Dongguan' },
            { code: 'FOS', name: 'Foshan' },
            { code: 'ZHO', name: 'Zhongshan' }
          ],
          'JS': [
            { code: 'NAN', name: 'Nanjing' },
            { code: 'SUZ', name: 'Suzhou' },
            { code: 'WUX', name: 'Wuxi' },
            { code: 'CHA', name: 'Changzhou' },
            { code: 'XUZ', name: 'Xuzhou' }
          ]
        },
        'BR': {
          'SP': [
            { code: 'SAO', name: 'São Paulo' },
            { code: 'CAM', name: 'Campinas' },
            { code: 'SAN', name: 'Santo André' },
            { code: 'OSA', name: 'Osasco' },
            { code: 'RIB', name: 'Ribeirão Preto' }
          ],
          'RJ': [
            { code: 'RIO', name: 'Rio de Janeiro' },
            { code: 'NIT', name: 'Niterói' },
            { code: 'DUQ', name: 'Duque de Caxias' },
            { code: 'NOV', name: 'Nova Iguaçu' },
            { code: 'SAO', name: 'São Gonçalo' }
          ],
          'MG': [
            { code: 'BEL', name: 'Belo Horizonte' },
            { code: 'UBE', name: 'Uberlândia' },
            { code: 'CON', name: 'Contagem' },
            { code: 'JUI', name: 'Juiz de Fora' },
            { code: 'BET', name: 'Betim' }
          ]
        },
        'MX': {
          'DIF': [
            { code: 'MEX', name: 'Mexico City' }
          ],
          'JAL': [
            { code: 'GUA', name: 'Guadalajara' },
            { code: 'ZAP', name: 'Zapopan' },
            { code: 'TLA', name: 'Tlaquepaque' },
            { code: 'TLA', name: 'Tonalá' },
            { code: 'PUE', name: 'Puerto Vallarta' }
          ],
          'NLE': [
            { code: 'MON', name: 'Monterrey' },
            { code: 'SAN', name: 'San Pedro Garza García' },
            { code: 'SAN', name: 'San Nicolás de los Garza' },
            { code: 'SAN', name: 'Santa Catarina' },
            { code: 'GUA', name: 'Guadalupe' }
          ]
        },
        'IT': {
          'LOM': [
            { code: 'MIL', name: 'Milan' },
            { code: 'BER', name: 'Bergamo' },
            { code: 'BRE', name: 'Brescia' },
            { code: 'PAV', name: 'Pavia' },
            { code: 'CRE', name: 'Cremona' }
          ],
          'LAZ': [
            { code: 'ROM', name: 'Rome' },
            { code: 'LAT', name: 'Latina' },
            { code: 'FRO', name: 'Frosinone' },
            { code: 'VIT', name: 'Viterbo' },
            { code: 'RIE', name: 'Rieti' }
          ],
          'VEN': [
            { code: 'VEN', name: 'Venice' },
            { code: 'VER', name: 'Verona' },
            { code: 'PAD', name: 'Padua' },
            { code: 'VIC', name: 'Vicenza' },
            { code: 'TRE', name: 'Treviso' }
          ]
        },
        'ES': {
          'M': [
            { code: 'MAD', name: 'Madrid' }
          ],
          'B': [
            { code: 'BAR', name: 'Barcelona' },
            { code: 'BAD', name: 'Badalona' },
            { code: 'SAB', name: 'Sabadell' },
            { code: 'TER', name: 'Terrassa' },
            { code: 'SAN', name: 'Sant Cugat' }
          ],
          'SE': [
            { code: 'SEV', name: 'Seville' },
            { code: 'DOS', name: 'Dos Hermanas' },
            { code: 'ALC', name: 'Alcalá de Guadaíra' },
            { code: 'MAR', name: 'Mairena del Aljarafe' },
            { code: 'EST', name: 'Estepa' }
          ]
        },
        'NL': {
          'NH': [
            { code: 'AMS', name: 'Amsterdam' },
            { code: 'HAAR', name: 'Haarlem' },
            { code: 'ZAA', name: 'Zaandam' },
            { code: 'HOO', name: 'Hoofddorp' },
            { code: 'HIL', name: 'Hilversum' }
          ],
          'ZH': [
            { code: 'ROT', name: 'Rotterdam' },
            { code: 'HAG', name: 'The Hague' },
            { code: 'ZWO', name: 'Zoetermeer' },
            { code: 'DEL', name: 'Delft' },
            { code: 'LEI', name: 'Leiden' }
          ],
          'UT': [
            { code: 'UTR', name: 'Utrecht' },
            { code: 'AME', name: 'Amersfoort' },
            { code: 'VEEN', name: 'Veenendaal' },
            { code: 'ZEI', name: 'Zeist' },
            { code: 'NIE', name: 'Nieuwegein' }
          ]
        },
        'SE': {
          'ST': [
            { code: 'STO', name: 'Stockholm' },
            { code: 'SOL', name: 'Solna' },
            { code: 'SUND', name: 'Sundbyberg' },
            { code: 'LID', name: 'Lidingö' },
            { code: 'NAC', name: 'Nacka' }
          ],
          'VG': [
            { code: 'GOT', name: 'Gothenburg' },
            { code: 'BOR', name: 'Borås' },
            { code: 'TRO', name: 'Trollhättan' },
            { code: 'UDD', name: 'Uddevalla' },
            { code: 'LID', name: 'Lidköping' }
          ],
          'SK': [
            { code: 'MAL', name: 'Malmö' },
            { code: 'LUN', name: 'Lund' },
            { code: 'HEL', name: 'Helsingborg' },
            { code: 'KRI', name: 'Kristianstad' },
            { code: 'LAN', name: 'Landskrona' }
          ]
        },
        'NO': {
          'OS': [
            { code: 'OSL', name: 'Oslo' }
          ],
          'RO': [
            { code: 'STA', name: 'Stavanger' },
            { code: 'SAN', name: 'Sandnes' },
            { code: 'HAU', name: 'Haugesund' },
            { code: 'JER', name: 'Jørpeland' },
            { code: 'KLE', name: 'Klepp' }
          ],
          'HO': [
            { code: 'BER', name: 'Bergen' },
            { code: 'STO', name: 'Stord' },
            { code: 'LEI', name: 'Leirvik' },
            { code: 'ODD', name: 'Odda' },
            { code: 'VOS', name: 'Voss' }
          ]
        },
        'DK': {
          'H': [
            { code: 'COP', name: 'Copenhagen' },
            { code: 'FRED', name: 'Frederiksberg' },
            { code: 'GLO', name: 'Glostrup' },
            { code: 'TAA', name: 'Taastrup' },
            { code: 'BAL', name: 'Ballerup' }
          ],
          'M': [
            { code: 'ROS', name: 'Roskilde' },
            { code: 'KOR', name: 'Korsør' },
            { code: 'NAE', name: 'Næstved' },
            { code: 'SLG', name: 'Slagelse' },
            { code: 'HOL', name: 'Holbæk' }
          ]
        },
        'FI': {
          '18': [
            { code: 'HEL', name: 'Helsinki' },
            { code: 'ESP', name: 'Espoo' },
            { code: 'VAN', name: 'Vantaa' },
            { code: 'KAU', name: 'Kauniainen' },
            { code: 'KER', name: 'Kerava' }
          ],
          '11': [
            { code: 'TAM', name: 'Tampere' },
            { code: 'NOK', name: 'Nokia' },
            { code: 'KAN', name: 'Kangasala' },
            { code: 'ORI', name: 'Orivesi' },
            { code: 'YLÖ', name: 'Ylöjärvi' }
          ],
          '19': [
            { code: 'TUR', name: 'Turku' },
            { code: 'RAA', name: 'Raisio' },
            { code: 'NAA', name: 'Naantali' },
            { code: 'PAR', name: 'Pargas' },
            { code: 'KAU', name: 'Kaarina' }
          ]
        },
        'CH': {
          'ZH': [
            { code: 'ZUR', name: 'Zurich' },
            { code: 'WINT', name: 'Winterthur' },
            { code: 'UST', name: 'Uster' },
            { code: 'DUE', name: 'Dübendorf' },
            { code: 'DIE', name: 'Dietikon' }
          ],
          'BE': [
            { code: 'BER', name: 'Bern' },
            { code: 'THU', name: 'Thun' },
            { code: 'BIE', name: 'Biel' },
            { code: 'KON', name: 'Köniz' },
            { code: 'STE', name: 'Steffisburg' }
          ],
          'GE': [
            { code: 'GEN', name: 'Geneva' },
            { code: 'VER', name: 'Vernier' },
            { code: 'LAN', name: 'Lancy' },
            { code: 'ONE', name: 'Onex' },
            { code: 'MEY', name: 'Meyrin' }
          ]
        },
        'AT': {
          'W': [
            { code: 'VIE', name: 'Vienna' }
          ],
          'ST': [
            { code: 'GRA', name: 'Graz' },
            { code: 'LEO', name: 'Leoben' },
            { code: 'KAP', name: 'Kapfenberg' },
            { code: 'BAD', name: 'Bruck an der Mur' },
            { code: 'FEL', name: 'Feldbach' }
          ],
          'OÖ': [
            { code: 'LIN', name: 'Linz' },
            { code: 'WEL', name: 'Wels' },
            { code: 'STE', name: 'Steyr' },
            { code: 'LEO', name: 'Leonding' },
            { code: 'TRA', name: 'Traun' }
          ]
        },
        'RU': {
          'MOW': [
            { code: 'MOS', name: 'Moscow' },
            { code: 'ZEL', name: 'Zelenograd' },
            { code: 'TRO', name: 'Troitsk' }
          ],
          'SPE': [
            { code: 'SPB', name: 'Saint Petersburg' },
            { code: 'KOL', name: 'Kolpino' },
            { code: 'KRAS', name: 'Krasnoye Selo' }
          ],
          'NVS': [
            { code: 'NOV', name: 'Novosibirsk' },
            { code: 'BER', name: 'Berdsk' },
            { code: 'KOL', name: 'Koltsovo' }
          ],
          'SVE': [
            { code: 'EKB', name: 'Yekaterinburg' },
            { code: 'NIZ', name: 'Nizhny Tagil' },
            { code: 'KAM', name: 'Kamensk-Uralsky' }
          ]
        },
        'TR': {
          '34': [
            { code: 'IST', name: 'Istanbul' },
            { code: 'KAD', name: 'Kadıköy' },
            { code: 'BEY', name: 'Beyoğlu' },
            { code: 'USK', name: 'Üsküdar' },
            { code: 'SIS', name: 'Şişli' }
          ],
          '06': [
            { code: 'ANK', name: 'Ankara' },
            { code: 'CANK', name: 'Çankaya' },
            { code: 'KEÇ', name: 'Keçiören' },
            { code: 'YEN', name: 'Yenimahalle' }
          ],
          '35': [
            { code: 'IZM', name: 'İzmir' },
            { code: 'BOR', name: 'Bornova' },
            { code: 'KAR', name: 'Karşıyaka' },
            { code: 'KON', name: 'Konak' }
          ]
        },
        'SA': {
          '06': [
            { code: 'RYD', name: 'Riyadh' },
            { code: 'OLY', name: 'Olaya' },
            { code: 'MAL', name: 'Malaz' }
          ],
          '11': [
            { code: 'MEC', name: 'Mecca' },
            { code: 'JED', name: 'Jeddah' },
            { code: 'TAI', name: 'Taif' }
          ],
          '07': [
            { code: 'DAM', name: 'Dammam' },
            { code: 'KHO', name: 'Khobar' },
            { code: 'JUB', name: 'Jubail' }
          ]
        },
        'AE': {
          'DU': [
            { code: 'DXB', name: 'Dubai' },
            { code: 'BUR', name: 'Bur Dubai' },
            { code: 'DEI', name: 'Deira' }
          ],
          'AZ': [
            { code: 'ADH', name: 'Abu Dhabi' },
            { code: 'AL', name: 'Al Ain' },
            { code: 'MUS', name: 'Musaffah' }
          ],
          'SH': [
            { code: 'SHJ', name: 'Sharjah' },
            { code: 'AJM', name: 'Ajman' },
            { code: 'UMM', name: 'Umm Al Quwain' }
          ]
        },
        'ZA': {
          'GP': [
            { code: 'JHB', name: 'Johannesburg' },
            { code: 'PTA', name: 'Pretoria' },
            { code: 'SOW', name: 'Soweto' }
          ],
          'WC': [
            { code: 'CPT', name: 'Cape Town' },
            { code: 'STL', name: 'Stellenbosch' },
            { code: 'PAAR', name: 'Paarl' }
          ],
          'KZN': [
            { code: 'DBN', name: 'Durban' },
            { code: 'PMB', name: 'Pietermaritzburg' },
            { code: 'UMH', name: 'Umlazi' }
          ]
        },
        'KR': {
          '11': [
            { code: 'SEO', name: 'Seoul' },
            { code: 'GANG', name: 'Gangnam' },
            { code: 'JONG', name: 'Jongno' }
          ],
          '26': [
            { code: 'BUS', name: 'Busan' },
            { code: 'HAE', name: 'Haeundae' },
            { code: 'NAM', name: 'Nam-gu' }
          ],
          '41': [
            { code: 'SUW', name: 'Suwon' },
            { code: 'YONG', name: 'Yongin' },
            { code: 'SEON', name: 'Seongnam' }
          ]
        },
        'TH': {
          '10': [
            { code: 'BKK', name: 'Bangkok' },
            { code: 'WAT', name: 'Watthana' },
            { code: 'SATH', name: 'Sathon' }
          ],
          '20': [
            { code: 'PAT', name: 'Pattaya' },
            { code: 'BAN', name: 'Bang Lamung' },
            { code: 'SAT', name: 'Sattahip' }
          ],
          '50': [
            { code: 'CHM', name: 'Chiang Mai' },
            { code: 'MAE', name: 'Mae Rim' },
            { code: 'DOI', name: 'Doi Saket' }
          ]
        },
        'VN': {
          '43': [
            { code: 'HCM', name: 'Ho Chi Minh City' },
            { code: 'D1', name: 'District 1' },
            { code: 'D3', name: 'District 3' }
          ],
          '11': [
            { code: 'HAN', name: 'Hanoi' },
            { code: 'HOAN', name: 'Hoan Kiem' },
            { code: 'BA', name: 'Ba Dinh' }
          ],
          '12': [
            { code: 'HP', name: 'Hai Phong' },
            { code: 'HONG', name: 'Hong Bang' },
            { code: 'NGO', name: 'Ngo Quyen' }
          ]
        },
        'MY': {
          'KUL': [
            { code: 'KL', name: 'Kuala Lumpur' },
            { code: 'AMP', name: 'Ampang' },
            { code: 'BAN', name: 'Bangsar' }
          ],
          'PNG': [
            { code: 'GEOR', name: 'George Town' },
            { code: 'BUT', name: 'Butterworth' },
            { code: 'BAY', name: 'Bayan Lepas' }
          ],
          'JHR': [
            { code: 'JB', name: 'Johor Bahru' },
            { code: 'BAT', name: 'Batu Pahat' },
            { code: 'MUAR', name: 'Muar' }
          ]
        },
        'SG': {
          '01': [
            { code: 'ORC', name: 'Orchard' },
            { code: 'MAR', name: 'Marina Bay' },
            { code: 'RIV', name: 'River Valley' }
          ],
          '02': [
            { code: 'TAM', name: 'Tampines' },
            { code: 'BED', name: 'Bedok' },
            { code: 'PAS', name: 'Pasir Ris' }
          ],
          '03': [
            { code: 'WOOD', name: 'Woodlands' },
            { code: 'YIS', name: 'Yishun' },
            { code: 'SEM', name: 'Sembawang' }
          ]
        },
        'PH': {
          'NCR': [
            { code: 'MNL', name: 'Manila' },
            { code: 'MKT', name: 'Makati' },
            { code: 'BGC', name: 'BGC' }
          ],
          'CEB': [
            { code: 'CEB', name: 'Cebu City' },
            { code: 'LAP', name: 'Lapu-Lapu' },
            { code: 'MAN', name: 'Mandaue' }
          ],
          'DAV': [
            { code: 'DAV', name: 'Davao City' },
            { code: 'TAG', name: 'Tagum' },
            { code: 'PAN', name: 'Panabo' }
          ]
        },
        'ID': {
          'JK': [
            { code: 'JKT', name: 'Jakarta' },
            { code: 'JKS', name: 'Jakarta Selatan' },
            { code: 'JKU', name: 'Jakarta Utara' }
          ],
          'JB': [
            { code: 'BDG', name: 'Bandung' },
            { code: 'CIM', name: 'Cimahi' },
            { code: 'BAN', name: 'Banjar' }
          ],
          'JI': [
            { code: 'SBY', name: 'Surabaya' },
            { code: 'MAL', name: 'Malang' },
            { code: 'KED', name: 'Kediri' }
          ]
        },
        'PK': {
          'PB': [
            { code: 'LHR', name: 'Lahore' },
            { code: 'FSD', name: 'Faisalabad' },
            { code: 'RWP', name: 'Rawalpindi' }
          ],
          'SN': [
            { code: 'KHI', name: 'Karachi' },
            { code: 'HYD', name: 'Hyderabad' },
            { code: 'SUK', name: 'Sukkur' }
          ],
          'ICT': [
            { code: 'ISB', name: 'Islamabad' },
            { code: 'RWP', name: 'Rawalpindi' }
          ]
        },
        'BD': {
          'A': [
            { code: 'DAC', name: 'Dhaka' },
            { code: 'NAR', name: 'Narayanganj' },
            { code: 'GAZ', name: 'Gazipur' }
          ],
          'B': [
            { code: 'CTG', name: 'Chittagong' },
            { code: 'COX', name: 'Cox\'s Bazar' },
            { code: 'COM', name: 'Comilla' }
          ],
          'C': [
            { code: 'RAJ', name: 'Rajshahi' },
            { code: 'BOG', name: 'Bogra' },
            { code: 'PAB', name: 'Pabna' }
          ]
        },
        'AR': {
          'C': [
            { code: 'BA', name: 'Buenos Aires' },
            { code: 'PAL', name: 'Palermo' },
            { code: 'REC', name: 'Recoleta' }
          ],
          'B': [
            { code: 'LAP', name: 'La Plata' },
            { code: 'MAR', name: 'Mar del Plata' },
            { code: 'BAH', name: 'Bahía Blanca' }
          ],
          'X': [
            { code: 'COR', name: 'Córdoba' },
            { code: 'VIL', name: 'Villa María' },
            { code: 'RIO', name: 'Río Cuarto' }
          ]
        },
        'NZ': {
          'AUK': [
            { code: 'AKL', name: 'Auckland' },
            { code: 'MAN', name: 'Manukau' },
            { code: 'NOR', name: 'North Shore' }
          ],
          'WGN': [
            { code: 'WEL', name: 'Wellington' },
            { code: 'LOW', name: 'Lower Hutt' },
            { code: 'UPP', name: 'Upper Hutt' }
          ],
          'CAN': [
            { code: 'CHC', name: 'Christchurch' },
            { code: 'TIM', name: 'Timaru' },
            { code: 'ASH', name: 'Ashburton' }
          ]
        }
      }
    };
  },
  computed: {
    availableStates() {
      switch (this.selectedCountry) {
        case 'US':
          return this.usStates;
        case 'CA':
          return this.canadianProvinces;
        case 'GB':
          return this.ukRegions;
        case 'AU':
          return this.australianStates;
        case 'DE':
          return this.germanStates;
        case 'FR':
          return this.frenchRegions;
        case 'IN':
          return this.indianStates;
        case 'JP':
          return this.japanesePrefectures;
        case 'CN':
          return this.chineseProvinces;
        case 'BR':
          return this.brazilianStates;
        case 'MX':
          return this.mexicanStates;
        case 'IT':
          return this.italianRegions;
        case 'ES':
          return this.spanishProvinces;
        case 'NL':
          return this.dutchProvinces;
        case 'SE':
          return this.swedishCounties;
        case 'NO':
          return this.norwegianCounties;
        case 'DK':
          return this.danishRegions;
        case 'FI':
          return this.finnishRegions;
        case 'CH':
          return this.swissCantons;
        case 'AT':
          return this.austrianStates;
        case 'BE':
          return this.belgianProvinces;
        case 'PT':
          return this.portugueseRegions;
        case 'GR':
          return this.greekRegions;
        case 'PL':
          return this.polishVoivodeships;
        case 'CZ':
          return this.czechRegions;
        case 'HU':
          return this.hungarianCounties;
        case 'RO':
          return this.romanianCounties;
        case 'BG':
          return this.bulgarianProvinces;
        case 'HR':
          return this.croatianCounties;
        case 'IE':
          return this.irishCounties;
        case 'IS':
          return this.icelandicRegions;
        case 'LU':
          return this.luxembourgDistricts;
        case 'MT':
          return this.malteseRegions;
        case 'SK':
          return this.slovakRegions;
        case 'SI':
          return this.slovenianRegions;
        case 'EE':
          return this.estonianCounties;
        case 'LV':
          return this.latvianRegions;
        case 'LT':
          return this.lithuanianCounties;
        case 'RU':
          return this.russianRegions;
        case 'UA':
          return this.ukrainianOblasts;
        case 'TR':
          return this.turkishProvinces;
        case 'SA':
          return this.saudiProvinces;
        case 'AE':
          return this.uaeEmirates;
        case 'IL':
          return this.israeliDistricts;
        case 'ZA':
          return this.southAfricanProvinces;
        case 'NG':
          return this.nigerianStates;
        case 'KE':
          return this.kenyanCounties;
        case 'AR':
          return this.argentineProvinces;
        case 'CL':
          return this.chileanRegions;
        case 'CO':
          return this.colombianDepartments;
        case 'PE':
          return this.peruvianRegions;
        case 'VE':
          return this.venezuelanStates;
        case 'EC':
          return this.ecuadorianProvinces;
        case 'UY':
          return this.uruguayanDepartments;
        case 'PY':
          return this.paraguayanDepartments;
        case 'BO':
          return this.bolivianDepartments;
        case 'KR':
          return this.southKoreanProvinces;
        case 'TH':
          return this.thaiProvinces;
        case 'VN':
          return this.vietnameseProvinces;
        case 'MY':
          return this.malaysianStates;
        case 'SG':
          return this.singaporeRegions;
        case 'PH':
          return this.philippineProvinces;
        case 'ID':
          return this.indonesianProvinces;
        case 'PK':
          return this.pakistaniProvinces;
        case 'BD':
          return this.bangladeshiDivisions;
        case 'LK':
          return this.sriLankanProvinces;
        case 'NZ':
          return this.newZealandRegions;
        default:
          return [];
      }
    },
    availableCities() {
      if (this.majorCities[this.selectedCountry] && this.majorCities[this.selectedCountry][this.selectedState]) {
        return this.majorCities[this.selectedCountry][this.selectedState];
      }
      return [];
    },
    cityOptions() {
      return this.availableCities;
    },
    postalCodePlaceholder() {
      switch (this.selectedCountry) {
        case 'US':
          return '12345 or 12345-6789';
        case 'CA':
          return 'A1A 1A1';
        case 'GB':
          return 'SW1A 1AA';
        case 'AU':
          return '2000';
        case 'DE':
          return '10115';
        case 'FR':
          return '75001';
        case 'IN':
          return '110001';
        case 'JP':
          return '100-0001';
        case 'CN':
          return '100000';
        case 'BR':
          return '01310-100';
        case 'MX':
          return '01000';
        case 'IT':
          return '00118';
        case 'ES':
          return '28001';
        case 'NL':
          return '1012 AB';
        case 'SE':
          return '111 22';
        case 'NO':
          return '0001';
        case 'DK':
          return '1000';
        case 'FI':
          return '00100';
        case 'CH':
          return '8001';
        case 'AT':
          return '1010';
        case 'BE':
          return '1000';
        case 'PT':
          return '1000-001';
        case 'GR':
          return '101 00';
        case 'PL':
          return '00-001';
        case 'CZ':
          return '110 00';
        case 'HU':
          return '1011';
        case 'RO':
          return '010001';
        case 'BG':
          return '1000';
        case 'HR':
          return '10000';
        case 'IE':
          return 'D02 AF30';
        case 'IS':
          return '101';
        case 'LU':
          return 'L-1009';
        case 'MT':
          return 'VLT 1000';
        case 'SK':
          return '010 01';
        case 'SI':
          return '1000';
        case 'EE':
          return '10111';
        case 'LV':
          return 'LV-1001';
        case 'LT':
          return '01101';
        case 'RU':
          return '101000';
        case 'UA':
          return '01001';
        case 'TR':
          return '34000';
        case 'SA':
          return '11564';
        case 'AE':
          return '00000';
        case 'IL':
          return '9100001';
        case 'ZA':
          return '0001';
        case 'NG':
          return '900001';
        case 'KE':
          return '00100';
        case 'AR':
          return 'C1000';
        case 'CL':
          return '8320000';
        case 'CO':
          return '110111';
        case 'PE':
          return '15001';
        case 'VE':
          return '1010';
        case 'EC':
          return '090101';
        case 'UY':
          return '11000';
        case 'PY':
          return '0000';
        case 'BO':
          return '0000';
        case 'KR':
          return '03051';
        case 'TH':
          return '10100';
        case 'VN':
          return '100000';
        case 'MY':
          return '50000';
        case 'SG':
          return '018956';
        case 'PH':
          return '1000';
        case 'ID':
          return '10110';
        case 'PK':
          return '44000';
        case 'BD':
          return '1000';
        case 'LK':
          return '00100';
        case 'NZ':
          return '1010';
        default:
          return 'Enter postal code';
      }
    },
    postalCodePattern() {
      switch (this.selectedCountry) {
        case 'US':
          return '^[0-9]{5}(-[0-9]{4})?$';
        case 'CA':
          return '^[A-Za-z][0-9][A-Za-z] [0-9][A-Za-z][0-9]$';
        case 'GB':
          return '^[A-Z]{1,2}[0-9][A-Z0-9]? [0-9][ABD-HJLNP-UW-Z]{2}$';
        case 'AU':
          return '^[0-9]{4}$';
        case 'DE':
          return '^[0-9]{5}$';
        case 'FR':
          return '^[0-9]{5}$';
        case 'IN':
          return '^[0-9]{6}$';
        case 'JP':
          return '^[0-9]{3}-[0-9]{4}$';
        case 'CN':
          return '^[0-9]{6}$';
        case 'BR':
          return '^[0-9]{5}-[0-9]{3}$';
        case 'MX':
          return '^[0-9]{5}$';
        case 'IT':
          return '^[0-9]{5}$';
        case 'ES':
          return '^[0-9]{5}$';
        case 'NL':
          return '^[0-9]{4} [A-Z]{2}$';
        case 'SE':
          return '^[0-9]{3} [0-9]{2}$';
        case 'NO':
          return '^[0-9]{4}$';
        case 'DK':
          return '^[0-9]{4}$';
        case 'FI':
          return '^[0-9]{5}$';
        case 'CH':
          return '^[0-9]{4}$';
        case 'AT':
          return '^[0-9]{4}$';
        case 'BE':
          return '^[0-9]{4}$';
        case 'PT':
          return '^[0-9]{4}-[0-9]{3}$';
        case 'GR':
          return '^[0-9]{3} [0-9]{2}$';
        case 'PL':
          return '^[0-9]{2}-[0-9]{3}$';
        case 'CZ':
          return '^[0-9]{3} [0-9]{2}$';
        case 'HU':
          return '^[0-9]{4}$';
        case 'RO':
          return '^[0-9]{6}$';
        case 'BG':
          return '^[0-9]{4}$';
        case 'HR':
          return '^[0-9]{5}$';
        case 'IE':
          return '^[A-Z0-9]{7,8}$';
        case 'IS':
          return '^[0-9]{3}$';
        case 'LU':
          return '^L-[0-9]{4}$';
        case 'MT':
          return '^[A-Z]{3} [0-9]{4}$';
        case 'SK':
          return '^[0-9]{3} [0-9]{2}$';
        case 'SI':
          return '^[0-9]{4}$';
        case 'EE':
          return '^[0-9]{5}$';
        case 'LV':
          return '^LV-[0-9]{4}$';
        case 'LT':
          return '^[0-9]{5}$';
        case 'RU':
          return '^[0-9]{6}$';
        case 'UA':
          return '^[0-9]{5}$';
        case 'TR':
          return '^[0-9]{5}$';
        case 'SA':
          return '^[0-9]{5}(-[0-9]{4})?$';
        case 'AE':
          return '^[0-9]{5}$';
        case 'IL':
          return '^[0-9]{7}$';
        case 'ZA':
          return '^[0-9]{4}$';
        case 'NG':
          return '^[0-9]{6}$';
        case 'KE':
          return '^[0-9]{5}$';
        case 'AR':
          return '^[A-Z]?[0-9]{4}[A-Z]{0,3}$';
        case 'CL':
          return '^[0-9]{7}$';
        case 'CO':
          return '^[0-9]{6}$';
        case 'PE':
          return '^[0-9]{5}$';
        case 'VE':
          return '^[0-9]{4}$';
        case 'EC':
          return '^[0-9]{6}$';
        case 'UY':
          return '^[0-9]{5}$';
        case 'PY':
          return '^[0-9]{4}$';
        case 'BO':
          return '^[0-9]{4}$';
        case 'KR':
          return '^[0-9]{5}$';
        case 'TH':
          return '^[0-9]{5}$';
        case 'VN':
          return '^[0-9]{5,6}$';
        case 'MY':
          return '^[0-9]{5}$';
        case 'SG':
          return '^[0-9]{6}$';
        case 'PH':
          return '^[0-9]{4}$';
        case 'ID':
          return '^[0-9]{5}$';
        case 'PK':
          return '^[0-9]{5}$';
        case 'BD':
          return '^[0-9]{4}$';
        case 'LK':
          return '^[0-9]{5}$';
        case 'NZ':
          return '^[0-9]{4}$';
        default:
          return '';
      }
    }
  },
  watch: {
    country(newVal) {
      this.selectedCountry = newVal || 'US';
    },
    state(newVal) {
      this.selectedState = newVal;
    },
    city(newVal) {
      this.selectedCity = newVal;
    },
    postalCode(newVal) {
      const oldVal = this.selectedPostalCode;
      this.selectedPostalCode = newVal;
      // Reset validation when postal code changes externally
      if (oldVal !== newVal) {
        this.postalCodeError = null;
        this.postalCodeValid = false;
        // Re-validate if there's a value
        if (newVal && newVal.trim()) {
          this.$nextTick(() => {
            this.validatePostalCode();
          });
        }
      }
    }
  },
  methods: {
    onCountryChange(countryCode) {
      this.selectedCountry = countryCode;
      this.selectedCity = '';
      
      // Set default state to Utah when US is selected, otherwise clear state
      if (countryCode === 'US') {
        this.selectedState = 'UT';
        this.$emit('update:state', 'UT');
      } else {
        this.selectedState = '';
        this.$emit('update:state', '');
      }
      
      this.$emit('update:country', countryCode);
      this.$emit('update:city', '');
      this.emitAddressChange();
    },
    onStateChange(stateCode) {
      this.selectedState = stateCode;
      this.selectedCity = '';
      this.$emit('update:state', stateCode);
      this.$emit('update:city', '');
      
      // Re-validate postal code if it exists to check state match
      if (this.selectedPostalCode && this.selectedPostalCode.trim() && this.selectedCountry === 'US') {
        this.$nextTick(() => {
          this.validatePostalCode();
        });
      }
      
      this.emitAddressChange();
    },
    onCityChange(cityValue) {
      // Handle both SearchableDropdown input and text input
      if (cityValue !== undefined) {
        this.selectedCity = cityValue;
      }
      this.$emit('update:city', this.selectedCity);
      this.emitAddressChange();
    },
    onPostalCodeChange() {
      // Clear previous errors on input
      if (this.postalCodeError) {
        this.postalCodeError = null;
        this.postalCodeValid = false;
      }
      
      this.$emit('update:postalCode', this.selectedPostalCode);
      this.emitAddressChange();
      
      // Validate on change if postal code is complete
      if (this.selectedPostalCode && this.selectedPostalCode.trim().length >= 5) {
        this.validatePostalCode();
      }
    },
    validatePostalCode() {
      // Postal code validation and auto-fill
      // Order: Use zipcodes package FIRST for city/state/country/postalCode
      // Address Line 1 & 2 parsing uses addresser package (available via parseAddress method)
      // Reset validation state
      this.postalCodeError = null;
      this.postalCodeValid = false;
      
      if (!this.selectedPostalCode || !this.selectedPostalCode.trim()) {
        if (this.required) {
          this.postalCodeError = 'Postal code is required';
        }
        return;
      }
      
      const postalCode = this.selectedPostalCode.trim().toUpperCase();
      
      // First, try to detect if it's a US zip code using zipcodes package (even if country is not set to US)
      // This allows auto-detection of country from zip code
      const cleanZip = postalCode.replace(/[-\s]/g, '');
      if (/^\d{5}(\d{4})?$/.test(cleanZip)) {
        const zipInfo = zipcodes.lookup(cleanZip);
        if (zipInfo) {
          // Valid US zip code found - auto-fill country, state, and city using zipcodes
          this.validateUSZipCode(postalCode, zipInfo);
          return;
        }
      }
      
      // Validate based on country
      if (this.selectedCountry === 'US') {
        this.validateUSZipCode(postalCode);
      } else if (this.selectedCountry === 'CA') {
        this.validateCanadianPostalCode(postalCode);
      } else if (this.selectedCountry === 'GB') {
        this.validateUKPostcode(postalCode);
      } else {
        // For other countries, just validate format
        this.validatePostalCodeFormat(postalCode);
      }
    },
    validateUSZipCode(zipCode, zipInfo = null) {
      // Clean zip code (remove dashes and spaces)
      const cleanZip = zipCode.replace(/[-\s]/g, '');
      
      // Check format first
      if (!/^\d{5}(\d{4})?$/.test(cleanZip)) {
        this.postalCodeError = 'Invalid US zip code format. Use 12345 or 12345-6789';
        return;
      }
      
      // Use zipcodes package to validate (if not already provided)
      if (!zipInfo) {
        zipInfo = zipcodes.lookup(cleanZip);
      }
      
      if (!zipInfo) {
        this.postalCodeError = 'Invalid US zip code';
        return;
      }
      
      // Check if zip code matches selected state (only warn if state is already set and different)
      if (this.selectedState && zipInfo.state !== this.selectedState) {
        this.postalCodeError = `This zip code belongs to ${this.getStateName(zipInfo.state)}, not ${this.getStateName(this.selectedState)}`;
        return;
      }
      
      // Zip code is valid
      this.postalCodeValid = true;
      
      // Auto-fill country to US if not already set
      if (this.selectedCountry !== 'US') {
        this.selectedCountry = 'US';
        this.$emit('update:country', 'US');
      }
      
      // Auto-fill state from zip code (always update to match zip code)
      if (zipInfo.state) {
        if (this.selectedState !== zipInfo.state) {
          this.selectedState = zipInfo.state;
          this.$emit('update:state', zipInfo.state);
        }
      }
      
      // Auto-fill city from zip code (always update to match zip code)
      if (zipInfo.city) {
        if (this.selectedCity !== zipInfo.city) {
          this.selectedCity = zipInfo.city;
          this.$emit('update:city', zipInfo.city);
        }
      }
      
      this.emitAddressChange();
    },
    validateCanadianPostalCode(postalCode) {
      // Canadian postal code format: A1A 1A1
      const canadianPattern = /^[A-Z]\d[A-Z] ?\d[A-Z]\d$/;
      
      if (!canadianPattern.test(postalCode)) {
        this.postalCodeError = 'Invalid Canadian postal code format. Use A1A 1A1';
        return;
      }
      
      this.postalCodeValid = true;
    },
    validateUKPostcode(postcode) {
      // UK postcode format: SW1A 1AA
      const ukPattern = /^[A-Z]{1,2}\d[A-Z0-9]? ?\d[ABD-HJLNP-UW-Z]{2}$/i;
      
      if (!ukPattern.test(postcode)) {
        this.postalCodeError = 'Invalid UK postcode format. Use SW1A 1AA';
        return;
      }
      
      this.postalCodeValid = true;
    },
    validatePostalCodeFormat(postalCode) {
      // Basic format validation based on country pattern
      const pattern = this.postalCodePattern;
      
      if (pattern && pattern !== '.*') {
        const regex = new RegExp(pattern);
        if (!regex.test(postalCode)) {
          this.postalCodeError = `Invalid postal code format for ${this.getCountryName(this.selectedCountry)}`;
          return;
        }
      }
      
      this.postalCodeValid = true;
    },
    getStateName(stateCode) {
      const state = this.usStates.find(s => s.code === stateCode);
      return state ? state.name : stateCode;
    },
    getCountryName(countryCode) {
      const country = this.countryOptions.find(c => c.code === countryCode);
      return country ? country.name : countryCode;
    },
    parseAddress(fullAddress) {
      // Parse a full address string
      // Order: 
      // - Address Line 1 & 2: Use addresser package (address-parsing)
      // - City, State, Country, Postal Code: Use zipcodes package FIRST, then fallback to addresser
      if (!fullAddress || typeof fullAddress !== 'string') {
        return {
          address1: null,
          address2: null,
          city: null,
          state: null,
          postalCode: null,
          country: null
        };
      }

      try {
        const address = fullAddress.trim();
        
        // Step 1: Extract zip code and use zipcodes package FIRST
        const zipCodeMatch = address.match(/\b(\d{5}(-\d{4})?)\b/);
        let zipCodeInfo = null;
        let extractedZipCode = null;
        
        if (zipCodeMatch) {
          extractedZipCode = zipCodeMatch[1].replace(/-/g, '');
          zipCodeInfo = zipcodes.lookup(extractedZipCode);
        }
        
        // Step 2: Use addresser package to parse Address Line 1 & 2
        const parsed = addresser.parseAddress(address);
        
        if (!parsed) {
          // If addresser fails, return basic result with zipcodes data
          return {
            address1: null,
            address2: null,
            city: (zipCodeInfo && zipCodeInfo.city) || null,
            state: (zipCodeInfo && zipCodeInfo.state) || null,
            postalCode: extractedZipCode || null,
            country: (zipCodeInfo && 'US') || null
          };
        }
        
        // Address Line 1: Use addresser package
        const streetNumber = parsed.streetNumber || '';
        const streetName = parsed.streetName || '';
        const address1 = (streetNumber + ' ' + streetName).trim() || parsed.street || parsed.address || null;
        
        // Address Line 2: Use addresser package
        const address2 = parsed.unit || parsed.suite || parsed.apartment || parsed.address2 || null;
        
        // Step 3: For City, State, Country, Postal Code - Use zipcodes FIRST, then fallback to addresser
        // City: zipcodes FIRST, then addresser
        let city = null;
        if (zipCodeInfo && zipCodeInfo.city) {
          city = zipCodeInfo.city;
        } else {
          city = parsed.placeName || parsed.city || null;
        }
        
        // State: zipcodes FIRST, then addresser
        let state = null;
        if (zipCodeInfo && zipCodeInfo.state) {
          state = zipCodeInfo.state;
        } else {
          state = parsed.stateAbbreviation || parsed.state || parsed.province || null;
          // Convert full state name to abbreviation if needed
          if (state && state.length > 2) {
            const stateAbbr = this.getStateAbbreviationFromName(state);
            if (stateAbbr) {
              state = stateAbbr;
            }
          }
        }
        
        // Postal Code: zipcodes FIRST (extracted), then addresser
        let postalCode = null;
        if (extractedZipCode) {
          postalCode = extractedZipCode;
        } else {
          postalCode = parsed.zipCode || parsed.zip || parsed.postalCode || parsed.postal || null;
        }
        
        // Country: zipcodes FIRST (US if zipcode found), then addresser
        let country = null;
        if (zipCodeInfo) {
          country = 'US';
        } else {
          if (parsed.country) {
            country = this.getCountryCodeFromName(parsed.country) || parsed.country;
          } else if (parsed.stateAbbreviation || parsed.state) {
            country = 'US';
          }
        }
        
        return {
          address1: address1,
          address2: address2,
          city: city,
          state: state,
          postalCode: postalCode,
          country: country
        };
      } catch (error) {
        console.error('Error parsing address:', error);
        return {
          address1: null,
          address2: null,
          city: null,
          state: null,
          postalCode: null,
          country: null
        };
      }
    },
    getStateAbbreviationFromName(stateName) {
      // Helper to convert full state name to abbreviation
      const stateMap = {
        'alabama': 'AL', 'alaska': 'AK', 'arizona': 'AZ', 'arkansas': 'AR',
        'california': 'CA', 'colorado': 'CO', 'connecticut': 'CT', 'delaware': 'DE',
        'florida': 'FL', 'georgia': 'GA', 'hawaii': 'HI', 'idaho': 'ID',
        'illinois': 'IL', 'indiana': 'IN', 'iowa': 'IA', 'kansas': 'KS',
        'kentucky': 'KY', 'louisiana': 'LA', 'maine': 'ME', 'maryland': 'MD',
        'massachusetts': 'MA', 'michigan': 'MI', 'minnesota': 'MN', 'mississippi': 'MS',
        'missouri': 'MO', 'montana': 'MT', 'nebraska': 'NE', 'nevada': 'NV',
        'new hampshire': 'NH', 'new jersey': 'NJ', 'new mexico': 'NM', 'new york': 'NY',
        'north carolina': 'NC', 'north dakota': 'ND', 'ohio': 'OH', 'oklahoma': 'OK',
        'oregon': 'OR', 'pennsylvania': 'PA', 'rhode island': 'RI', 'south carolina': 'SC',
        'south dakota': 'SD', 'tennessee': 'TN', 'texas': 'TX', 'utah': 'UT',
        'vermont': 'VT', 'virginia': 'VA', 'washington': 'WA', 'west virginia': 'WV',
        'wisconsin': 'WI', 'wyoming': 'WY'
      };
      
      if (!stateName) return null;
      const normalized = stateName.toLowerCase().trim();
      
      // Check if already an abbreviation
      const usStates = ['AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'];
      if (usStates.includes(normalized.toUpperCase())) {
        return normalized.toUpperCase();
      }
      
      return stateMap[normalized] || null;
    },
    getCountryCodeFromName(countryName) {
      // Helper to convert country name to code
      const countryMap = {
        'united states': 'US', 'usa': 'US', 'us': 'US',
        'canada': 'CA',
        'united kingdom': 'GB', 'uk': 'GB', 'great britain': 'GB',
        'australia': 'AU',
        'germany': 'DE',
        'france': 'FR',
        'india': 'IN',
        'japan': 'JP',
        'china': 'CN',
        'brazil': 'BR',
        'mexico': 'MX',
        'italy': 'IT',
        'spain': 'ES',
        'netherlands': 'NL', 'holland': 'NL'
      };
      
      if (!countryName) return null;
      const normalized = countryName.toLowerCase().trim();
      
      // Check if already a code
      const countryCodes = ['US', 'CA', 'GB', 'AU', 'DE', 'FR', 'IN', 'JP', 'CN', 'BR', 'MX', 'IT', 'ES', 'NL'];
      if (countryCodes.includes(normalized.toUpperCase())) {
        return normalized.toUpperCase();
      }
      
      return countryMap[normalized] || null;
    },
    emitAddressChange() {
      this.$emit('address-change', {
        country: this.selectedCountry,
        state: this.selectedState,
        city: this.selectedCity,
        postalCode: this.selectedPostalCode
      });
    }
  },
  mounted() {
    // Set default country to US if not provided
    if (!this.selectedCountry) {
      this.selectedCountry = 'US';
      this.$emit('update:country', 'US');
    }
    
    // Set default state to Utah when country is US and no state is selected
    if (this.selectedCountry === 'US' && !this.selectedState) {
      this.selectedState = 'UT';
      this.$emit('update:state', 'UT');
    }
  }
};
</script>

<style src="@/components/styles/AddressSelector.css"></style>
